import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeOptions } from '@/i18n/chartText';
import { SHEIN_QTY_KEYS, SKU_PKG_TYPE_KEYS, TITLE_LANG_KEYS } from '@/i18n/pkgOptionKeys';
import { useNavigate } from 'react-router-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import PublishScrollJumpFab from '@/components/publish/PublishScrollJumpFab';
import AttributeField from '@/shein-platform/modules/products/components/AttributeField';
import CategoryCascaderSelect from '@/shein-platform/components/CategoryCascaderSelect';
import SearchableSelect from '@/shein-platform/modules/products/components/SearchableSelect';
import SearchableMultiSelect from '@/shein-platform/modules/products/components/SearchableMultiSelect';
import SheinDateTimePicker from '@/shein-platform/modules/products/components/SheinDateTimePicker';
import SpecSupplySection from '@/shein-platform/modules/products/components/SpecSupplySection';
import SkuRrpFillPanel from '@/shein-platform/modules/products/components/SkuRrpFillPanel';
import SizeChartPanel from '@/shein-platform/modules/products/components/SizeChartPanel';
import PackageBatchModal, {
  WEIGHT_UNIT_OPTIONS,
  DIM_UNIT_OPTIONS,
  QUANTITY_UNIT_OPTIONS,
  PACKAGE_TYPE_OPTIONS,
} from '@/shein-platform/modules/products/components/PackageBatchModal';
import { applyPackageBatchBySize, applyPackageBatchToAllSkus } from '@/shein-platform/utils/sheinPackageBatch';
import SaveAttributeTemplateMetaModal from '@/shein-platform/modules/templates/SaveAttributeTemplateMetaModal';
import SaveProductTemplateMetaModal from '@/shein-platform/modules/templates/SaveProductTemplateMetaModal';
import { buildSheinPath } from '@/shein-platform/constants/sheinRoutes';
import { useSheinShop } from '@/shein-platform/context/SheinShopContext';
import { flattenBrandRows, flattenIpRows } from '@/shein-platform/utils/sheinBrandIp';
import { flattenLeafCategories } from '@/shein-platform/utils/sheinCategoryTree';
import {
  calcPublishCompletion,
  parseAttributeTemplate,
  partitionProductAttrsForDisplay,
  attributeDisplayLabel,
  attributeValueDisplayLabel,
  resolveSkcColorAgainstTemplate,
} from '@/shein-platform/utils/sheinAttributeTemplate';
import { layoutAttributeFields } from '@/shein-platform/utils/sheinAttributeLayout';
import { isSheinAttrRequiredMark } from '@/shein-platform/utils/sheinRequiredAttrGroups';
import { parseCustomAttributePermission } from '@/shein-platform/utils/sheinCustomAttribute';
import {
  applyProductAttributeTemplatePayload,
  countFilledProductAttributes,
  alignAttributesToCategoryTemplate,
  pickFilledProductAttributes,
  remapProductAttributesAcrossCategories,
  remapSizeChartAcrossCategories,
  remapSkcListAcrossCategories,
} from '@/shein-platform/utils/sheinProductAttributeTemplate';
import {
  readProductTemplatePublishHandoff,
  scheduleClearProductTemplatePublishHandoff,
} from '@/shein-platform/utils/sheinProductTemplatePublishHandoff';
import { pickAutoRuleTemplate, pickHighestPriorityTemplate, sortTemplatesByPriority } from '@/shein-platform/utils/sheinTemplateAutoRule';
import { styleNoFromSupplierCode } from '@/shein-platform/utils/sheinStyleNo';
import {
  SHEIN_TEMPLATE_SCOPES,
  SHEIN_TEMPLATE_SCOPE_LABELS,
} from '@/shein-platform/constants/sheinTemplateScope';
import {
  checkTemplateVersions,
  collectAppliedTemplateRefs,
  conflictFieldKey,
  conflictToastMessage,
  extractAttrTemplatePatch,
  extractSizeChartTemplatePatch,
  isMissingTemplateError,
  mergeProductTemplateIntoForm,
  parseTemplateVersion,
  partitionTemplateVersionConflicts,
  TEMPLATE_VERSION_TYPES,
} from '@/shein-platform/utils/sheinTemplateVersionGuard';
import {
  diffProductAttributeChanges,
  diffSizeChartChanges,
  indexAttributeChangeHints,
} from '@/shein-platform/utils/sheinTemplateChangeDiff';
import TemplateVersionConflictMark from '@/shein-platform/modules/products/components/TemplateVersionConflictMark';
import TemplateChangeDiffList from '@/shein-platform/modules/products/components/TemplateChangeDiffList';
import {
  customLabelFromKey,
  isCustomSpecKey,
  pruneSizeChartForSubmit,
  skuSizeKey,
} from '@/shein-platform/utils/sheinSizeChart';
import { buildSheinPublishPayload, samplePublishPayloadCurrency } from '@/shein-platform/utils/buildSheinPublishPayload';
import {
  applyRrpListToFormSkcList,
  normalizeRrpRule,
  validatePublishRrp,
} from '@/shein-platform/utils/sheinRrpPublish';
import {
  buildSheinEditPayload,
  enrichFormWithPlatformSkcIds,
  validateEditPlatformIds,
} from '@/shein-platform/utils/buildSheinEditPayload';
import {
  buildSaveSalePricePayload,
  buildUpdateCostPayload,
  chunkUpdateCostPayload,
  collectSkuPriceChanges,
  extractPriceLocksFromSearch,
  interpretPriceUpdateResponse,
  isEmptyPartialEditError,
  validatePriceChangesAgainstLocks,
} from '@/shein-platform/utils/buildSheinPriceUpdate';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import PriceUpdateConfirmModal from '@/shein-platform/modules/products/components/PriceUpdateConfirmModal';
import {
  collectProductAttributePairs,
  isValueAllowedByLinkedRule,
  parseAssociatedAttributeRules,
} from '@/shein-platform/utils/sheinAssociatedAttributeRules';
import {
  EMPTY_SKU_PACKAGE_CAPS,
  extractPublishCurrencies,
  getFieldRule,
  parseFillInStandardMap,
  pickPublishCurrency,
  resolveSkuPackageCapabilities,
} from '@/shein-platform/utils/sheinPublishFillInStandard';
import {
  extractSheinPublishedSpu,
  extractSheinPublishFailMessage,
  isSheinPublishSuccess,
} from '@/shein-platform/utils/sheinPublishResult';
import {
  extractSheinPublishFieldErrors,
  fieldErrorsToMap,
} from '@/shein-platform/utils/mapSheinPublishFieldErrors';
import { loadShopPublishCurrencies } from '@/shein-platform/utils/resolveSheinPublishCurrency';
import { listEnabledSites, pickSiteListCurrency } from '@/shein-platform/utils/sheinSiteList';
import { validateSheinPublishForm } from '@/shein-platform/utils/validateSheinPublishForm';
import { persistSheinFormImageSlots, sanitizeSheinFormImageSlots } from '@/shein-platform/utils/sanitizeSheinFormImageSlots';
import { runSheinPublishPreflight } from '@/shein-platform/utils/sheinPublishPreflight';
import { generateSheinPublishAiTitle } from '@/shein-platform/utils/sheinPublishAiTitle';
import {
  assessSheinPublishPrintInfringement,
  EMPTY_SHEIN_PRINT_INFRINGEMENT,
  normalizeSheinPrintInfringement,
  resolveFirstPrintImageUrl,
} from '@/shein-platform/utils/sheinPublishPrintInfringement';
import { buildCreditActionHint } from '@/constants/ipeCredits';
import { confirmIpeCreditAction } from '@/utils/ipeCreditConfirm';
import {
  applyPrintNamesToSheinForm,
  enrichSheinFormPrintNames,
  printNamesFromSheinPrintImages,
} from '@/pages/ipe/workbench/utils/applyListingItemToSheinPublishForm';
import { sheinPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import { useUnsavedLeaveGuard } from '@/shein-platform/hooks/useUnsavedLeaveGuard';

function FieldErrorText({ message }) {
  if (!message) return null;
  return <div className="spo-field__error" role="alert">{message}</div>;
}

const TITLE_LANGUAGE_OPTIONS = [
  { value: 'en', label: '英语' },
  { value: 'zh-cn', label: '简体中文' },
  { value: 'de', label: '德语' },
  { value: 'fr', label: '法语' },
  { value: 'es', label: '西班牙语' },
  { value: 'it', label: '意大利语' },
  { value: 'pt', label: '葡萄牙语' },
  { value: 'ja', label: '日语' },
  { value: 'ko', label: '韩语' },
];


const ORIGIN_OPTIONS = [
  'China', 'Vietnam', 'Bangladesh', 'India', 'Turkey', 'Indonesia', 'Cambodia', 'Pakistan', 'United States', 'Other',
];


const SECTIONS = [
  { id: 'basic', titleKey: 'sectionBasic' },
  { id: 'attrs', titleKey: 'sectionAttrs' },
  { id: 'spec', titleKey: 'sectionSpec' },
  { id: 'size', titleKey: 'sectionSizeChart' },
  { id: 'sales', titleKey: 'sectionSales' },
];

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  skcList.forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

function parseSizeSelectionKey(sizeId) {
  const key = String(sizeId || '');
  if (isCustomSpecKey(key)) {
    const label = customLabelFromKey(key);
    return {
      sizeValueId: '',
      sizeIsCustom: true,
      sizeExtraValue: label,
      sizeLabel: label,
    };
  }
  return {
    sizeValueId: key,
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
  };
}

/** 类目树里 product_type_id=0 表示无类型，不能当作有效匹配键。 */
function normalizePublishProductTypeId(value) {
  const raw = String(value || '').trim();
  if (!raw || raw === '0') return '';
  return raw;
}

/** 发品可选：单店铺独享 + 团队店铺共享（官方需先复制到店铺库）。 */
async function loadPublishProductTemplates(shopId, productTypeId) {
  const query = {
    productTypeId,
    page: 1,
    pageSize: 100,
  };
  const [personalRes, teamRes] = await Promise.all([
    sheinPlatformAPI.listProductTemplates(shopId, {
      ...query,
      scope: SHEIN_TEMPLATE_SCOPES.personal,
    }),
    sheinPlatformAPI.listProductTemplates(shopId, {
      ...query,
      scope: SHEIN_TEMPLATE_SCOPES.team,
    }).catch(() => ({ items: [] })),
  ]);
  const merged = [];
  const seen = new Set();
  for (const item of [
    ...(Array.isArray(personalRes?.items) ? personalRes.items : []),
    ...(Array.isArray(teamRes?.items) ? teamRes.items : []),
  ]) {
    const id = String(item?.id || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    merged.push(item);
  }
  return sortTemplatesByPriority(merged);
}

function productTemplateOptionLabel(item, t) {
  const name = item?.name || String(item?.id || '');
  const scope = String(item?.scope || '').toUpperCase();
  const scopeKey = scope === 'PERSONAL'
    ? 'productTemplate.scopePersonal'
    : (scope === 'TEAM'
      ? 'productTemplate.scopeTeam'
      : (scope === 'OFFICIAL' ? 'productTemplate.scopeOfficial' : ''));
  const scopeLabel = scopeKey
    ? t(scopeKey, { defaultValue: SHEIN_TEMPLATE_SCOPE_LABELS[scope] || '' })
    : '';
  const priority = item?.sortOrder != null
    ? t('publishBasic.priorityOnly', { order: item.sortOrder })
    : '';
  const parts = [priority, scopeLabel].filter(Boolean);
  return parts.length ? `${name}（${parts.join(' · ')}）` : name;
}

function buildSkuRowsForSizes(existingRows = [], sizeIds = [], sizeAttr, defaults = {}) {
  if (!sizeIds.length) {
    // 编辑回填：已有多行 SKU 时绝不能压成一行（模板过滤尺码后 sizeIds 可能暂时为空）
    if (existingRows.length > 1) {
      return existingRows.map((row) => ({
        ...row,
        sizeAttributeId: sizeAttr?.attributeId || row.sizeAttributeId || '',
      }));
    }
    // 未选尺码时保留一行空 SKU，便于仅按颜色填价库存
    const first = existingRows[0];
    return [first
      ? {
        ...first,
        sizeValueId: '',
        sizeIsCustom: false,
        sizeExtraValue: '',
        sizeLabel: '',
        sizeAttributeId: sizeAttr?.attributeId || first.sizeAttributeId || '',
      }
      : createSkuRow({
        sizeAttributeId: sizeAttr?.attributeId || '',
        price: defaults.price || '',
        stock: defaults.stock || '',
        weight: defaults.weight || '',
        currency: defaults.currency || '',
      })];
  }
  return sizeIds.map((sizeId) => {
    const parsed = parseSizeSelectionKey(sizeId);
    const found = existingRows.find((row) => skuSizeKey(row) === String(sizeId));
    if (found) {
      return {
        ...found,
        sizeAttributeId: sizeAttr?.attributeId || found.sizeAttributeId || '',
        sizeValueId: parsed.sizeValueId,
        sizeIsCustom: parsed.sizeIsCustom,
        sizeExtraValue: parsed.sizeExtraValue,
        sizeLabel: parsed.sizeLabel
          || found.sizeLabel
          || sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name
          || '',
      };
    }
    const platformName = !parsed.sizeIsCustom
      ? (sizeAttr?.values?.find((v) => String(v.id) === parsed.sizeValueId)?.name || '')
      : parsed.sizeLabel;
    return createSkuRow({
      sizeAttributeId: sizeAttr?.attributeId || '',
      sizeValueId: parsed.sizeValueId,
      sizeIsCustom: parsed.sizeIsCustom,
      sizeExtraValue: parsed.sizeExtraValue,
      sizeLabel: platformName,
      price: defaults.price || '',
      stock: defaults.stock || '',
      weight: defaults.weight || '',
      currency: defaults.currency || '',
    });
  });
}

function createSkuRow(partial = {}) {
  return {
    id: `sku-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    sizeAttributeId: '',
    sizeValueId: '',
    sizeIsCustom: false,
    sizeExtraValue: '',
    sizeLabel: '',
    supplierSku: '',
    price: '',
    specialPrice: '',
    currency: '',
    stock: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    quantity: '',
    quantityUnit: '1',
    packageType: '',
    mallState: 1,
    skuImage: null,
    skuCode: '',
    siteRrpInfoList: [],
    ...partial,
  };
}

function createSkc(partial = {}) {
  return {
    id: `skc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    colorAttributeId: '',
    colorValueId: '',
    colorLabel: '',
    colorCode: '',
    colorIsCustom: false,
    colorExtraValue: '',
    supplierCode: '',
    skcName: '',
    mainImage: null,
    showroomSlots: {},
    detailImages: [],
    squareImage: null,
    colorBlockImage: null,
    skuRows: [createSkuRow()],
    ...partial,
  };
}

function deriveFormCurrency(initial = {}) {
  const top = String(initial.currency || '').trim();
  if (top) return top;
  for (const skc of initial.skcList || []) {
    for (const sku of skc.skuRows || []) {
      const code = String(sku?.currency || '').trim();
      if (code) return code;
    }
  }
  return '';
}

/** 兼容后端旧形态 {attributeValueId,attributeValueName} → 前端 AttributeField 选中值 */
/** 未选仓库时默认第一个商家仓（提交用 warehouseCode，展示只用名称） */
function withDefaultWarehouse(form, warehouses = []) {
  const next = form && typeof form === 'object' ? { ...form } : {};
  const rows = Array.isArray(warehouses) ? warehouses : [];
  if (!rows.length) return next;
  const codes = rows
    .map((item) => String(item?.id || item?.warehouseCode || '').trim())
    .filter(Boolean);
  const current = String(next.warehouseId || '').trim();
  if (current && codes.includes(current)) {
    if (!String(next.warehouseName || '').trim()) {
      const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
      next.warehouseName = hit?.name || hit?.warehouseName || '';
    }
    return next;
  }
  const first = rows[0];
  const id = String(first?.id || first?.warehouseCode || '').trim();
  if (!id) return next;
  next.warehouseId = id;
  next.warehouseName = first?.name || first?.warehouseName || '';
  return next;
}

function normalizeProductAttributes(raw = {}) {
  const out = {};
  Object.entries(raw || {}).forEach(([id, value]) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      const asComposition = value.some((row) => (
        row && typeof row === 'object' && ('percent' in row || 'attributeExtraValue' in row || 'valueId' in row)
      ));
      if (asComposition) {
        out[id] = value
          .map((row) => {
            if (row != null && (typeof row === 'string' || typeof row === 'number')) {
              return { valueId: String(row), percent: '' };
            }
            if (!row || typeof row !== 'object') return null;
            const valueId = row.valueId ?? row.attributeValueId;
            if (valueId == null || valueId === '') return null;
            return {
              valueId: String(valueId),
              percent: String(row.percent ?? row.attributeExtraValue ?? ''),
            };
          })
          .filter(Boolean);
        return;
      }
      out[id] = value
        .map((row) => {
          if (row && typeof row === 'object') return String(row.valueId ?? row.attributeValueId ?? '');
          return String(row ?? '');
        })
        .filter(Boolean);
      return;
    }
    if (typeof value === 'object') {
      const valueId = value.valueId ?? value.attributeValueId;
      const percent = value.percent ?? value.attributeExtraValue;
      if (valueId != null && valueId !== '') {
        // 有比例（含 0）或显式 percent 字段 → 成分行；否则先保留 valueId，模板加载后再按 mode 转换
        if (percent != null && String(percent).trim() !== '') {
          out[id] = [{ valueId: String(valueId), percent: String(percent) }];
        } else {
          out[id] = String(valueId);
        }
      } else if (value.attributeExtraValue != null && String(value.attributeExtraValue).trim() !== '') {
        out[id] = String(value.attributeExtraValue);
      }
      return;
    }
    out[id] = value;
  });
  return out;
}

function emptyForm(initial = {}) {
  return {
    spuName: initial.spuName || '',
    title: initial.title || '',
    titleLanguage: initial.titleLanguage || 'en',
    description: initial.description || '',
    categoryId: initial.categoryId || '',
    productTypeId: initial.productTypeId || '',
    categoryPathLabel: initial.categoryPathLabel || '',
    supplierCode: initial.supplierCode || '',
    brandCode: initial.brandCode || '',
    originCountry: initial.originCountry || '',
    ipId: initial.ipId || '',
    ipName: initial.ipName || '',
    ipNameCn: initial.ipNameCn || '',
    productAttributes: normalizeProductAttributes(initial.productAttributes || {}),
    skcList: Array.isArray(initial.skcList) ? initial.skcList : [],
    selectedSizeIds: Array.isArray(initial.selectedSizeIds)
      ? initial.selectedSizeIds
      : deriveSelectedSizeIds(initial.skcList),
    printImages: Array.isArray(initial.printImages) ? initial.printImages : [],
    ipePrintImageNames: Array.isArray(initial.ipePrintImageNames)
      ? initial.ipePrintImageNames
      : printNamesFromSheinPrintImages(initial.printImages),
    ipePrintInfringement: normalizeSheinPrintInfringement(
      initial.ipePrintInfringement || EMPTY_SHEIN_PRINT_INFRINGEMENT,
    ),
    sheinVideos: Array.isArray(initial.sheinVideos) ? initial.sheinVideos : [],
    sizeChart: initial.sizeChart || {},
    sizeSaleAttributeId: initial.sizeSaleAttributeId || '',
    supplyPrice: initial.supplyPrice || '',
    currency: deriveFormCurrency(initial),
    stockQty: initial.stockQty || '',
    weightGrams: initial.weightGrams || '',
    weightUnit: initial.weightUnit || 'g',
    pkgLength: initial.pkgLength || '10',
    pkgWidth: initial.pkgWidth || '10',
    pkgHeight: initial.pkgHeight || '10',
    dimUnit: initial.dimUnit || 'cm',
    warehouseId: initial.warehouseId || '',
    warehouseName: initial.warehouseName || '',
    selectedSites: Array.isArray(initial.selectedSites) ? initial.selectedSites : [],
    mainSite: initial.mainSite || 'shein',
    shelfWay: initial.shelfWay || '1',
    hopeOnSaleDate: initial.hopeOnSaleDate || '',
    // 建议零售价（SPU 级 suggested_retail_price）：仅当发布规范 suggest_price 要求时下发
    suggestedRetailPrice: initial.suggestedRetailPrice || '',
    suggestedRetailPriceCurrency: initial.suggestedRetailPriceCurrency || '',
  };
}

export default function ProductPublishEditor({
  mode = 'create',
  spuName: editSpuName = '',
  draftId: initialDraftId = '',
  recordId: initialRecordId = '',
  productTemplateId: initialProductTemplateId = '',
  fromProductTemplate = false,
  referenceShopId = '',
}) {
  const { t } = useTranslation('workbench');
  const isEdit = mode === 'edit';
  const navigate = useNavigate();
  const { activeShop } = useSheinShop();
  const shopId = referenceShopId || activeShop?.id;
  const shopCaps = useMemo(
    () => sheinShopCapabilities(activeShop?.shopType),
    [activeShop?.shopType],
  );
  const priceMode = shopCaps.canUpdateSalePrice ? 'sale' : (shopCaps.canUpdateCost ? 'cost' : '');
  const needsFormBootstrap = Boolean(
    !isEdit && (initialDraftId || initialProductTemplateId || fromProductTemplate),
  );
  const [draftId, setDraftId] = useState(() => (!isEdit && initialDraftId ? String(initialDraftId) : ''));
  const [draftLoading, setDraftLoading] = useState(() => needsFormBootstrap);
  const [draftHydrated, setDraftHydrated] = useState(() => !needsFormBootstrap);
  const [activeSection, setActiveSection] = useState('basic');
  const [form, setForm] = useState(() => emptyForm({}));
  const [meta, setMeta] = useState({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
  const [metaLoading, setMetaLoading] = useState(false);
  /** 半托管/全托管：发布规范 【currency】；自运营：query-site-list 自营站点币种 */
  const [publishCurrencies, setPublishCurrencies] = useState([]);
  const [publishCurrencyLoading, setPublishCurrencyLoading] = useState(false);
  /** 按末级类目加载的发布规范：门控 SKU 件数 / 包装类型 / 建议零售价，并提供规范币种 */
  const [skuPkgCaps, setSkuPkgCaps] = useState(EMPTY_SKU_PACKAGE_CAPS);
  const [skuPkgCapsLoading, setSkuPkgCapsLoading] = useState(false);
  /** 当前末级类目的发布规范原文（规范币种 / suggest_price 等按类目变化，必须带 category_id 才有） */
  const [categoryStandard, setCategoryStandard] = useState(null);
  /** 关联属性必填规则：attributeId → { allowedValueIds } */
  const [linkedAttrRules, setLinkedAttrRules] = useState(() => new Map());
  const [linkedAttrLoading, setLinkedAttrLoading] = useState(false);
  const [attrTpl, setAttrTpl] = useState(null);
  const [attrLoading, setAttrLoading] = useState(false);
  const [attrError, setAttrError] = useState('');
  const [attrsExpanded, setAttrsExpanded] = useState(false);
  const [attrTemplates, setAttrTemplates] = useState([]);
  const [selectedAttrTemplateId, setSelectedAttrTemplateId] = useState('');
  const [selectedSizeChartTemplateId, setSelectedSizeChartTemplateId] = useState('');
  const [productTemplates, setProductTemplates] = useState([]);
  const [selectedProductTemplateId, setSelectedProductTemplateId] = useState('');
  const [selectedProductTemplateVersion, setSelectedProductTemplateVersion] = useState(null);
  const [selectedAttrTemplateVersion, setSelectedAttrTemplateVersion] = useState(null);
  const [selectedSizeChartTemplateVersion, setSelectedSizeChartTemplateVersion] = useState(null);
  const [versionConflicts, setVersionConflicts] = useState({});
  const [changePreviews, setChangePreviews] = useState({});
  const [reloadingConflictType, setReloadingConflictType] = useState('');
  const [productTemplateBusy, setProductTemplateBusy] = useState(false);
  const [attrTemplateBusy, setAttrTemplateBusy] = useState(false);
  const [attrSaveModalOpen, setAttrSaveModalOpen] = useState(false);
  const [productTplSaveModalOpen, setProductTplSaveModalOpen] = useState(false);
  /** 分类下可自定义属性值的 attributeId 集合 */
  const [customAttrPerm, setCustomAttrPerm] = useState({});
  /** 类目切换后待自动引用的 productTypeId（空=不自动） */
  const pendingAttrAutoForRef = useRef('');
  /** 类目切换后待自动套用产品模板的 productTypeId */
  const pendingProductTplAutoForRef = useRef('');
  /** 切类目时暂存旧类目已填参数，待新 schema 就绪后按相似字段回填 */
  const pendingCategoryPreserveRef = useRef(null);
  const categorySelectSeqRef = useRef(0);
  const attrTemplatesLoadedForRef = useRef('');
  /** 上次属性库自动套用所用的款号（小写）；用于货号变更时重匹配，避免编辑回填误套 */
  const lastAttrAutoStyleKeyRef = useRef(null);
  const attrTplRef = useRef(null);
  const productTemplateHandoffAppliedRef = useRef('');
  const [submitting, setSubmitting] = useState(false);
  const [aiTitleBusy, setAiTitleBusy] = useState(false);
  const [printInfringementBusy, setPrintInfringementBusy] = useState(false);
  const [submitHint, setSubmitHint] = useState('');
  /** 编辑改价确认：{ submitForm, changes } */
  const [priceConfirm, setPriceConfirm] = useState(null);
  /** 发品建议零售价填写规则（随品牌变化） */
  const [rrpRule, setRrpRule] = useState(null);
  const [rrpRuleLoading, setRrpRuleLoading] = useState(false);

  const [savingDraft, setSavingDraft] = useState(false);
  const [loadingSpu, setLoadingSpu] = useState(false);
  const editBaselineRef = useRef(null);
  /**
   * 本次提交的商品发布规范（query-publish-fill-in-standard）。
   * 非服装类目会额外要求上架要求 / 最小备货数量 / 建议零售价，
   * 组包时必须按规范决定传或不传，故预检通过后缓存原文。
   */
  const publishStandardRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pkgBatchMode, setPkgBatchMode] = useState(null);

  const formBootLoading = !draftHydrated
    || draftLoading
    || Boolean(isEdit && loadingSpu)
    || metaLoading
    || publishCurrencyLoading;
  const { allowLeave: allowUnsavedLeave, runWithoutUnloadPrompt } = useUnsavedLeaveGuard({
    value: form,
    enabled: Boolean(shopId),
    loading: formBootLoading,
    settleMs: 1600,
  });

  const brandOptions = useMemo(() => flattenBrandRows(meta.brands), [meta.brands]);
  const ipOptions = useMemo(() => flattenIpRows(meta.ips), [meta.ips]);
  const categoryOptions = useMemo(() => flattenLeafCategories(meta.categories), [meta.categories]);
  const siteOptions = useMemo(() => listEnabledSites(meta.sites), [meta.sites]);

  /** 半托管/全托管走发布规范币种；自运营走站点列表（随已选站点对齐） */
  const platformCurrency = useMemo(() => {
    if (shopCaps.usesPriceInfo) {
      return pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites: form.selectedSites,
      });
    }
    return pickPublishCurrency(publishCurrencies, form.currency);
  }, [form.currency, form.selectedSites, meta.sites, publishCurrencies, shopCaps.usesPriceInfo]);

  const applyPublishCurrency = (code) => {
    const next = String(code || '').trim().toUpperCase();
    if (!next) return;
    setForm((prev) => {
      if (String(prev.currency || '').trim().toUpperCase() === next) return prev;
      return {
        ...prev,
        currency: next,
        skcList: (prev.skcList || []).map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => ({
            ...sku,
            currency: next,
          })),
        })),
      };
    });
  };

  const selectedSitesKey = useMemo(
    () => (Array.isArray(form.selectedSites) ? form.selectedSites.map(String).filter(Boolean).join('|') : ''),
    [form.selectedSites],
  );

  // 币种就绪后回写表单：半托管/全托管按发布规范；自运营按站点列表（已选站点优先）。
  useEffect(() => {
    if (!publishCurrencies.length) return;
    const selectedSites = selectedSitesKey ? selectedSitesKey.split('|') : [];
    const next = shopCaps.usesPriceInfo
      ? pickSiteListCurrency(meta.sites, {
        preferred: form.currency,
        selectedSites,
      })
      : pickPublishCurrency(publishCurrencies, form.currency);
    if (!next) return;
    if (String(form.currency || '').trim().toUpperCase() === String(next).trim().toUpperCase()) return;
    applyPublishCurrency(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 规范/站点币种或回填变化时对齐
  }, [publishCurrencies, form.currency, selectedSitesKey, meta.sites, shopCaps.usesPriceInfo]);

  const warehouseOptions = useMemo(() => {
    const rows = Array.isArray(meta.warehouses) ? meta.warehouses : [];
    const mapped = rows
      .map((item) => {
        const id = String(item.id || item.warehouseCode || '');
        const name = String(item.name || item.warehouseName || '').trim();
        return {
          value: id,
          // 下拉只展示仓库名称；提交仍用 value=warehouseCode
          label: name || id,
          name: name || id,
        };
      })
      .filter((item) => item.value);
    if (form.warehouseId && !mapped.some((item) => item.value === String(form.warehouseId))) {
      const fallbackName = String(form.warehouseName || '').trim();
      mapped.unshift({
        value: String(form.warehouseId),
        label: fallbackName || String(form.warehouseId),
        name: fallbackName || String(form.warehouseId),
      });
    }
    return mapped;
  }, [meta.warehouses, form.warehouseId, form.warehouseName]);
  const multiWarehouse = warehouseOptions.length > 1;

  // 库存仓库：默认第一个。编辑回填可能清空 warehouseId，需在列表就绪或选中失效时再默认一次。
  useEffect(() => {
    const rows = Array.isArray(meta.warehouses) ? meta.warehouses : [];
    if (!rows.length) return;
    setForm((prev) => {
      const current = String(prev.warehouseId || '').trim();
      const codes = rows
        .map((item) => String(item.id || item.warehouseCode || '').trim())
        .filter(Boolean);
      if (current && codes.includes(current)) {
        // 补全名称（仅有 code 时）
        if (!String(prev.warehouseName || '').trim()) {
          const hit = rows.find((item) => String(item.id || item.warehouseCode || '') === current);
          const name = hit?.name || hit?.warehouseName || '';
          if (name) return { ...prev, warehouseName: name };
        }
        return prev;
      }
      const first = rows[0];
      const id = String(first.id || first.warehouseCode || '').trim();
      if (!id) return prev;
      return {
        ...prev,
        warehouseId: id,
        warehouseName: first.name || first.warehouseName || '',
      };
    });
  }, [meta.warehouses, form.warehouseId]);
  const colorAttr = attrTpl?.skcSaleAttrs?.[0] || null;
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0] || null;
  const sizeMeasureAttrsBase = attrTpl?.sizeAttrs;
  const sizeMeasureAttrs = useMemo(
    () => (Array.isArray(sizeMeasureAttrsBase) ? sizeMeasureAttrsBase : []).map((attr) => {
      const id = String(attr.attributeId || '');
      if (!id || !linkedAttrRules.has(id)) return attr;
      if (attr.required) return attr;
      return { ...attr, required: true };
    }),
    [sizeMeasureAttrsBase, linkedAttrRules],
  );
  const colorLabel = attributeDisplayLabel(colorAttr) || t('publishSpec.mainSpec');
  const sizeLabel = attributeDisplayLabel(sizeAttr) || t('publishSpec.otherSpec');
  const completion = useMemo(() => calcPublishCompletion(form, attrTpl), [form, attrTpl]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev?.[key] && !prev?.form) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });
  };

  const setAttrValue = (attributeId, value) => {
    setForm((prev) => ({
      ...prev,
      productAttributes: { ...prev.productAttributes, [attributeId]: value },
    }));
  };

  /** 自定义属性值写入本地属性模板，供下拉即时可选 */
  const appendCustomAttrValue = (attributeId, opt) => {
    if (!attributeId || !opt?.id) return;
    setAttrTpl((prev) => {
      if (!prev) return prev;
      const patchList = (list) => (list || []).map((attr) => {
        if (String(attr.attributeId) !== String(attributeId)) return attr;
        if ((attr.values || []).some((v) => String(v.id) === String(opt.id))) return attr;
        return {
          ...attr,
          values: [...(attr.values || []), {
            id: String(opt.id),
            name: opt.name,
            nameCn: opt.nameCn || opt.name,
          }],
        };
      });
      return {
        ...prev,
        productAttrs: patchList(prev.productAttrs),
        requiredProduct: patchList(prev.requiredProduct),
        mainAttrs: patchList(prev.mainAttrs),
        otherAttrs: patchList(prev.otherAttrs),
        optionalProduct: patchList(prev.optionalProduct),
        skcSaleAttrs: patchList(prev.skcSaleAttrs),
        skuSaleAttrs: patchList(prev.skuSaleAttrs),
      };
    });
  };

  const updateSkc = (skcId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => (skc.id === skcId ? { ...skc, ...patch } : skc)),
    }));
  };

  const updatePrintImages = (imgs) => {
    const list = Array.isArray(imgs) ? imgs : [];
    setForm((prev) => applyPrintNamesToSheinForm({
      ...prev,
      printImages: list,
      ipePrintImageNames: printNamesFromSheinPrintImages(list),
      ipePrintInfringement: { ...EMPTY_SHEIN_PRINT_INFRINGEMENT },
    }));
  };

  const onVideosChange = (videos) => {
    setForm((prev) => ({
      ...prev,
      sheinVideos: Array.isArray(videos) ? videos : [],
    }));
    setFieldErrors((prev) => {
      if (!prev?.sheinVideos) return prev;
      const next = { ...prev };
      delete next.sheinVideos;
      return next;
    });
  };

  const updateSku = (skcId, skuId, patch) => {
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        return {
          ...skc,
          skuRows: skc.skuRows.map((sku) => (sku.id === skuId ? { ...sku, ...patch } : sku)),
        };
      }),
    }));
  };

  // 进入发品页即可查 RRP 规则（brand_code 非必传）；选了品牌后再带 brand_code 刷新
  const [rrpRuleNonce, setRrpRuleNonce] = useState(0);
  useEffect(() => {
    if (!shopId) {
      setRrpRule(null);
      setRrpRuleLoading(false);
      return undefined;
    }
    let cancelled = false;
    setRrpRuleLoading(true);
    const brand = String(form.brandCode || '').trim();
    sheinPlatformAPI.queryRecommendRetailPriceRule(shopId, brand ? { brand_code: brand } : {})
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res?.info?.data ?? res;
        setRrpRule(normalizeRrpRule(data));
      })
      .catch(() => {
        if (!cancelled) setRrpRule(null);
      })
      .finally(() => {
        if (!cancelled) setRrpRuleLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.brandCode, rrpRuleNonce]);

  const skuRowCount = useMemo(
    () => (form.skcList || []).reduce((n, skc) => n + ((skc.skuRows || []).length), 0),
    [form.skcList],
  );

  // 规则 / 上架站点 / SKU 行变化 → 对齐各 SKU 的 siteRrpInfoList（新建发品）
  useEffect(() => {
    if (isEdit || !rrpRule) return;
    setForm((prev) => {
      const nextList = applyRrpListToFormSkcList(prev.skcList, rrpRule, prev.selectedSites);
      let changed = prev.skcList?.length !== nextList.length;
      if (!changed) {
        for (let i = 0; i < nextList.length; i += 1) {
          const aRows = prev.skcList[i]?.skuRows || [];
          const bRows = nextList[i]?.skuRows || [];
          if (aRows.length !== bRows.length) {
            changed = true;
            break;
          }
          for (let j = 0; j < bRows.length; j += 1) {
            if (JSON.stringify(aRows[j]?.siteRrpInfoList || [])
              !== JSON.stringify(bRows[j]?.siteRrpInfoList || [])) {
              changed = true;
              break;
            }
          }
          if (changed) break;
        }
      }
      if (!changed) return prev;
      return { ...prev, skcList: nextList };
    });
  }, [rrpRule, form.selectedSites, isEdit, skuRowCount]);

  const batchUpdateSku = (patchOrMapper = {}) => {
    if (typeof patchOrMapper === 'function') {
      setForm((prev) => ({
        ...prev,
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: (skc.skuRows || []).map((sku) => {
            const patch = patchOrMapper(skc, sku);
            if (!patch || typeof patch !== 'object') return sku;
            return { ...sku, ...patch };
          }),
        })),
      }));
      showToast(t('templateEditor.skuRuleDone'), 'success');
      return;
    }
    const next = {};
    ['price', 'stock', 'supplierSku', 'weight', 'length', 'width', 'height', 'quantity', 'quantityUnit', 'packageType'].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(patchOrMapper, key)) next[key] = patchOrMapper[key];
    });
    if (!Object.keys(next).length) return;
    setForm((prev) => ({
      ...prev,
      skcList: prev.skcList.map((skc) => ({
        ...skc,
        skuRows: (skc.skuRows || []).map((sku) => ({ ...sku, ...next })),
      })),
    }));
    showToast(t('publishPkg.appliedAll'), 'success');
  };

  const removeSku = (skcId, skuId) => {
    setForm((prev) => {
      const nextSkcList = prev.skcList.map((skc) => {
        if (skc.id !== skcId) return skc;
        const nextRows = skc.skuRows.filter((sku) => sku.id !== skuId);
        return {
          ...skc,
          skuRows: nextRows.length
            ? nextRows
            : [createSkuRow({ sizeAttributeId: sizeAttr?.attributeId || '' })],
        };
      });
      return {
        ...prev,
        skcList: nextSkcList,
        selectedSizeIds: deriveSelectedSizeIds(nextSkcList),
      };
    });
  };

  const skuDefaultsFromForm = (prev) => ({
    price: prev.supplyPrice || '',
    stock: prev.stockQty || '',
    weight: prev.weightGrams || '',
    currency: prev.currency || '',
  });

  const onColorsChange = (colorKeys) => {
    const platformIds = new Set((colorAttr?.values || []).map((v) => String(v.id)));
    const safeKeys = [...new Set((colorKeys || []).map(String).filter((key) => {
      if (!key) return false;
      if (key.startsWith('custom:')) return Boolean(key.slice('custom:'.length));
      return platformIds.has(key);
    }))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      const sizeIds = (prev.selectedSizeIds || [])
        .map(String)
        .filter((id) => (sizeAttr?.values || []).some((v) => String(v.id) === id));
      const nextSkcList = safeKeys.map((colorKey) => {
        const isCustom = colorKey.startsWith('custom:');
        let colorExtraValue = '';
        if (isCustom) {
          try {
            colorExtraValue = decodeURIComponent(colorKey.slice('custom:'.length));
          } catch {
            colorExtraValue = colorKey.slice('custom:'.length);
          }
        }
        const existing = prev.skcList.find((skc) => {
          if (isCustom) {
            return Boolean(skc.colorIsCustom || skc.colorExtraValue)
              && String(skc.colorExtraValue || skc.colorLabel || '') === colorExtraValue;
          }
          return String(skc.colorValueId || '') === colorKey && !skc.colorIsCustom;
        });
        const val = !isCustom
          ? colorAttr?.values?.find((v) => String(v.id) === colorKey)
          : null;
        const colorLabel = isCustom
          ? colorExtraValue
          : (val?.nameCn || val?.name || existing?.colorLabel || '');
        if (existing) {
          return {
            ...existing,
            colorAttributeId: colorAttr?.attributeId || existing.colorAttributeId,
            colorValueId: isCustom ? '' : colorKey,
            colorLabel,
            colorCode: existing.colorCode || '',
            colorIsCustom: isCustom,
            colorExtraValue: isCustom ? colorExtraValue : '',
            skuRows: buildSkuRowsForSizes(existing.skuRows, sizeIds, sizeAttr, defaults),
          };
        }
        return createSkc({
          colorAttributeId: colorAttr?.attributeId || '',
          colorValueId: isCustom ? '' : colorKey,
          colorLabel,
          colorCode: '',
          colorIsCustom: isCustom,
          colorExtraValue: isCustom ? colorExtraValue : '',
          skuRows: buildSkuRowsForSizes([], sizeIds, sizeAttr, defaults),
        });
      });
      return { ...prev, skcList: nextSkcList, selectedSizeIds: sizeIds };
    });
  };

  const onSizesChange = (sizeIds) => {
    const allowed = new Set((sizeAttr?.values || []).map((v) => String(v.id)));
    // SHEIN 尺码不支持自定义，仅允许平台属性值
    const safeIds = [...new Set((sizeIds || []).map(String).filter((id) => (
      !isCustomSpecKey(id) && allowed.has(id)
    )))];
    setForm((prev) => {
      const defaults = skuDefaultsFromForm(prev);
      return {
        ...prev,
        selectedSizeIds: safeIds,
        sizeSaleAttributeId: sizeAttr?.attributeId || prev.sizeSaleAttributeId || '',
        skcList: prev.skcList.map((skc) => ({
          ...skc,
          skuRows: buildSkuRowsForSizes(skc.skuRows, safeIds, sizeAttr, defaults),
        })),
      };
    });
  };

  const updateSizeCell = (sizeValueId, attrId, value) => {
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: {
        ...prev.sizeChart,
        [sizeValueId]: {
          ...(prev.sizeChart?.[sizeValueId] || {}),
          [attrId]: value,
        },
      },
    }));
  };

  const applySizeChart = (nextChart, options = {}) => {
    const replace = options?.replace === true;
    setForm((prev) => ({
      ...prev,
      sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
      sizeChart: replace
        ? { ...(nextChart || {}) }
        : {
          ...(prev.sizeChart || {}),
          ...(nextChart || {}),
        },
    }));
  };

  useEffect(() => {
    if (isEdit) {
      setDraftLoading(false);
      setDraftHydrated(true);
      return undefined;
    }

    // 草稿优先
    if (initialDraftId) {
      if (!shopId) {
        setDraftLoading(false);
        setDraftHydrated(true);
        return undefined;
      }
      let cancelled = false;
      setDraftLoading(true);
      setDraftHydrated(false);
      sheinPlatformAPI.getPublishDraft(shopId, initialDraftId)
        .then((res) => {
          if (cancelled) return;
          const formData = res?.form && typeof res.form === 'object' ? res.form : {};
          setDraftId(String(res?.id || initialDraftId));
          setForm(sanitizeSheinFormImageSlots(emptyForm(formData)));
          setSelectedAttrTemplateId(String(
            formData.selectedAttrTemplateId || res?.selectedAttrTemplateId || '',
          ).trim());
          setSelectedSizeChartTemplateId(String(
            formData.selectedSizeChartTemplateId || res?.selectedSizeChartTemplateId || '',
          ).trim());
          setSelectedProductTemplateId(String(formData.selectedProductTemplateId || '').trim());
          setDraftHydrated(true);
        })
        .catch((e) => {
          if (cancelled) return;
          const msg = e?.status === 404 || /不存在|not found/i.test(String(e?.message || ''))
            ? t('publishFlow.draftGone')
            : (e?.message || t('publishFlow.draftLoadFail'));
          showToast(msg, 'error');
          setDraftId('');
          setDraftHydrated(true);
        })
        .finally(() => {
          if (!cancelled) setDraftLoading(false);
        });
      return () => { cancelled = true; };
    }

    // 列表「发布」：按产品模板 ID 拉取
    if (initialProductTemplateId) {
      if (!shopId) {
        setDraftLoading(false);
        setDraftHydrated(true);
        return undefined;
      }
      let cancelled = false;
      setDraftLoading(true);
      setDraftHydrated(false);
      sheinPlatformAPI.getProductTemplate(shopId, initialProductTemplateId)
        .then((res) => {
          if (cancelled) return;
          const item = res?.item || res || {};
          const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
          const formData = rawPayload.form && typeof rawPayload.form === 'object'
            ? rawPayload.form
            : rawPayload;
          setForm(sanitizeSheinFormImageSlots(emptyForm({
            ...formData,
            categoryId: formData.categoryId || item.categoryId || '',
            productTypeId: formData.productTypeId || item.productTypeId || '',
            categoryPathLabel: formData.categoryPathLabel || item.categoryPathLabel || '',
          })));
          setSelectedAttrTemplateId(String(
            rawPayload.selectedAttrTemplateId || formData.selectedAttrTemplateId || '',
          ).trim());
          setSelectedSizeChartTemplateId(String(
            rawPayload.selectedSizeChartTemplateId || formData.selectedSizeChartTemplateId || '',
          ).trim());
          setSelectedProductTemplateId(String(item.id || initialProductTemplateId));
          setDraftHydrated(true);
          showToast(t('publishFlow.broughtIn', { name: item.name || initialProductTemplateId }), 'success');
        })
        .catch((e) => {
          if (cancelled) return;
          showToast(e?.message || t('templateEditor.templateLoadFail'), 'error');
          setDraftHydrated(true);
        })
        .finally(() => {
          if (!cancelled) setDraftLoading(false);
        });
      return () => { cancelled = true; };
    }

    // 编辑页「发布」：按 token 读取表单快照（IndexedDB / localStorage）
    if (fromProductTemplate) {
      const handoffToken = String(fromProductTemplate || '').trim();
      if (productTemplateHandoffAppliedRef.current === handoffToken) {
        setDraftLoading(false);
        setDraftHydrated(true);
        return undefined;
      }
      let cancelled = false;
      setDraftLoading(true);
      setDraftHydrated(false);
      readProductTemplatePublishHandoff(handoffToken)
        .then((formData) => {
          if (cancelled) return;
          if (formData) {
            productTemplateHandoffAppliedRef.current = handoffToken;
            setForm(sanitizeSheinFormImageSlots(emptyForm(formData)));
            setSelectedAttrTemplateId(String(formData.selectedAttrTemplateId || '').trim());
            setSelectedSizeChartTemplateId(String(formData.selectedSizeChartTemplateId || '').trim());
            setSelectedProductTemplateId(String(formData.selectedProductTemplateId || '').trim());
            scheduleClearProductTemplatePublishHandoff(handoffToken);
            showToast(t('publishFlow.broughtContent'), 'success');
          } else {
            showToast(t('publishFlow.templateMissing'), 'warning');
          }
          setDraftHydrated(true);
        })
        .catch(() => {
          if (cancelled) return;
          showToast(t('publishFlow.templateMissing'), 'warning');
          setDraftHydrated(true);
        })
        .finally(() => {
          if (!cancelled) setDraftLoading(false);
        });
      return () => { cancelled = true; };
    }

    setDraftLoading(false);
    setDraftHydrated(true);
    return undefined;
  }, [isEdit, initialDraftId, initialProductTemplateId, fromProductTemplate, shopId, t]);

  useEffect(() => {
    if (!shopId) {
      setMeta({ categories: null, brands: null, ips: null, sites: null, warehouses: [] });
      setPublishCurrencies([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    setPublishCurrencyLoading(true);
    Promise.all([
      sheinPlatformAPI.getShopCategories(shopId),
      sheinPlatformAPI.getShopBrands(shopId),
      sheinPlatformAPI.getShopIps(shopId),
      sheinPlatformAPI.getShopSiteList(shopId),
      sheinPlatformAPI.getShopWarehouses(shopId).catch(() => ({ warehouses: [] })),
    ])
      .then(async ([catRes, brandRes, ipRes, siteRes, warehouseRes]) => {
        if (cancelled) return;
        const warehouses = Array.isArray(warehouseRes?.warehouses)
          ? warehouseRes.warehouses
          : [];
        // 规范币种按末级类目才有，店铺级（不带 category_id）拿不到，
        // 因此这里只解析自运营站点币种；半托管/全托管等选完类目由类目规范回写。
        const loaded = await loadShopPublishCurrencies({
          shopCaps,
          siteList: siteRes?.siteList ?? null,
          fillInStandard: null,
          refreshSiteList: () => sheinPlatformAPI.getShopSiteList(shopId, { refresh: true }),
        });
        if (cancelled) return;
        setMeta({
          categories: catRes?.categories ?? null,
          brands: brandRes?.brands ?? null,
          ips: ipRes?.ips ?? null,
          sites: loaded.siteList,
          warehouses,
        });
        setPublishCurrencies(loaded.currencies);
        // 供货价币种要等选完类目才可能拿到，此处只对自运营站点币种缺失告警
        if (loaded.emptyWarning && shopCaps.usesPriceInfo) {
          // eslint-disable-next-line no-console
          console.warn('[shein] publish currency empty', loaded.source, loaded.emptyWarning);
          showToast(loaded.emptyWarning, 'warning');
        }
      })
      .catch((e) => {
        if (!cancelled) showToast(e?.message || t('templateEditor.shopOptionsFail'), 'error');
      })
      .finally(() => {
        if (!cancelled) {
          setMetaLoading(false);
          setPublishCurrencyLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [shopId, shopCaps.mode, t]);

  useEffect(() => {
    if (!shopId || !draftHydrated) return undefined;
    const attrId = String(selectedAttrTemplateId || '').trim();
    if (!attrId) return undefined;
    let cancelled = false;
    sheinPlatformAPI.getProductAttributeTemplate(shopId, attrId)
      .then((res) => {
        if (cancelled) return;
        setSelectedAttrTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || !isMissingTemplateError(e)) return;
        setSelectedAttrTemplateId('');
        setSelectedAttrTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, draftHydrated, selectedAttrTemplateId]);

  useEffect(() => {
    if (!shopId || !draftHydrated) return undefined;
    const sizeId = String(selectedSizeChartTemplateId || '').trim();
    if (!sizeId) return undefined;
    let cancelled = false;
    sheinPlatformAPI.getSizeChartTemplate(shopId, sizeId)
      .then((res) => {
        if (cancelled) return;
        setSelectedSizeChartTemplateVersion((prev) => (
          prev != null ? prev : parseTemplateVersion(res?.item?.version)
        ));
      })
      .catch((e) => {
        if (cancelled || !isMissingTemplateError(e)) return;
        setSelectedSizeChartTemplateId('');
        setSelectedSizeChartTemplateVersion(null);
      });
    return () => { cancelled = true; };
  }, [shopId, draftHydrated, selectedSizeChartTemplateId]);

  /**
   * 产品模板版本兜底。
   *
   * 从产品模板列表「发布商品」（?productTemplateId=）或模板页本地传递进入时只带模板 ID、
   * 不带 version，发品前乐观锁会把 expectedVersion=null 当成版本不一致，报「产品模板已被更新，
   * 请重新加载对应区域」；用户点重新加载又会用模板内容覆盖刚填好的表单。
   * 这里在进入发品页时补一次版本（等价于以打开时的版本为基准），
   * 之后模板真的被改，仍然会正常报冲突、仍然提醒重新加载。
   */
  useEffect(() => {
    if (!shopId || !draftHydrated) return undefined;
    const id = String(selectedProductTemplateId || '').trim();
    if (!id || selectedProductTemplateVersion != null) return undefined;
    let cancelled = false;
    sheinPlatformAPI.getProductTemplate(shopId, id)
      .then((res) => {
        if (cancelled) return;
        const version = parseTemplateVersion(res?.item?.version);
        if (version != null) setSelectedProductTemplateVersion(version);
      })
      .catch(() => {
        /* 取不到版本时不强制锁定，交给发品前校验与后端兜底 */
      });
    return () => { cancelled = true; };
  }, [shopId, draftHydrated, selectedProductTemplateId, selectedProductTemplateVersion]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setAttrTpl(null);
      setAttrError('');
      return undefined;
    }
    if (!form.productTypeId) {
      setAttrTpl(null);
      setAttrError(t('templateEditor.noProductType'));
      return undefined;
    }
    let cancelled = false;
    setAttrLoading(true);
    setAttrError('');
    // 官方入参为 product_type_id_list，不是 category_id；forceRefresh 避免空/脏缓存
    sheinPlatformAPI.getAttributeTemplate(shopId, {
      product_type_id_list: [Number(form.productTypeId)],
    }, { forceRefresh: true })
      .then((res) => {
        if (cancelled) return;
        const parsed = parseAttributeTemplate(res, { productTypeId: form.productTypeId });
        setAttrTpl(parsed);
        // 材质/成分等控件要 [{valueId,percent}]；编辑回填常是 string / string[]
        // 切类目时按名称把旧类目相似字段回填到新 schema
        const stash = pendingCategoryPreserveRef.current;
        let preserveToast = '';
        if (stash) {
          pendingCategoryPreserveRef.current = null;
          const remappedAttrs = remapProductAttributesAcrossCategories({
            prevAttributes: stash.productAttributes,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSize = remapSizeChartAcrossCategories({
            prevSizeChart: stash.sizeChart,
            prevSelectedSizeIds: stash.selectedSizeIds,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const remappedSkc = remapSkcListAcrossCategories({
            prevSkcList: stash.skcList,
            prevAttrTpl: stash.prevAttrTpl,
            nextAttrTpl: parsed,
          });
          const selectedSizeIds = remappedSize.selectedSizeIds.length
            ? remappedSize.selectedSizeIds
            : remappedSkc.selectedSizeIds;
          if (
            remappedAttrs.preservedCount > 0
            || remappedSize.preservedCount > 0
            || remappedSkc.preservedCount > 0
          ) {
            pendingAttrAutoForRef.current = '';
            pendingProductTplAutoForRef.current = '';
            const parts = [];
            if (remappedAttrs.preservedCount > 0) parts.push(t('templateEditor.keptSimilar', { count: remappedAttrs.preservedCount }));
            if (remappedSkc.preservedCount > 0) parts.push(t('templateEditor.keptMainSpec', { count: remappedSkc.preservedCount }));
            if (remappedSize.preservedCount > 0) parts.push(t('templateEditor.sizeChartWord'));
            preserveToast = parts.length ? t('templateEditor.keptPrefix', { detail: parts.join('、') }) : '';
          }
          setForm((prev) => ({
            ...prev,
            productAttributes: alignAttributesToCategoryTemplate(remappedAttrs.attributes, parsed),
            sizeChart: remappedSize.sizeChart,
            selectedSizeIds,
            skcList: remappedSkc.skcList,
          }));
        } else {
          setForm((prev) => {
            const aligned = alignAttributesToCategoryTemplate(prev.productAttributes, parsed);
            if (aligned === prev.productAttributes) return prev;
            return { ...prev, productAttributes: aligned };
          });
        }
        if (preserveToast) showToast(preserveToast, 'success');
        const total = (parsed.productAttrs?.length || 0)
          + (parsed.skcSaleAttrs?.length || 0)
          + (parsed.skuSaleAttrs?.length || 0)
          + (parsed.sizeAttrs?.length || 0);
        if (total === 0) {
          setAttrError(t('templateEditor.attrEmptyCat'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAttrTpl(null);
        setAttrError(e?.message || t('templateEditor.attrLoadFail'));
      })
      .finally(() => {
        if (!cancelled) setAttrLoading(false);
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId, form.productTypeId, t]);

  useEffect(() => {
    if (!shopId || !form.productTypeId) {
      setAttrTemplates([]);
      attrTemplatesLoadedForRef.current = '';
      // 切类目时由 onCategoryChange 清空选中；此处勿清，避免草稿/模板回填被冲掉
      return undefined;
    }
    const productTypeId = String(form.productTypeId);
    let cancelled = false;
    attrTemplatesLoadedForRef.current = '';
    sheinPlatformAPI.listProductAttributeTemplates(shopId, {
      productTypeId,
      page: 1,
      pageSize: 100,
    })
      .then((res) => {
        if (cancelled) return;
        setAttrTemplates(Array.isArray(res?.items) ? res.items : []);
        attrTemplatesLoadedForRef.current = productTypeId;
      })
      .catch(() => {
        if (!cancelled) {
          setAttrTemplates([]);
          attrTemplatesLoadedForRef.current = productTypeId;
        }
      });
    return () => { cancelled = true; };
  }, [shopId, form.productTypeId]);

  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setCustomAttrPerm({});
      return undefined;
    }
    let cancelled = false;
    sheinPlatformAPI.getCustomAttributePermission(shopId, {
      category_id_list: [Number(form.categoryId)],
    })
      .then((res) => {
        if (cancelled) return;
        setCustomAttrPerm(parseCustomAttributePermission(res));
      })
      .catch(() => {
        if (!cancelled) setCustomAttrPerm({});
      });
    return () => { cancelled = true; };
  }, [shopId, form.categoryId]);

  // 按末级类目拉取发布规范（quantity_info / package_type 必须带 category_id）
  useEffect(() => {
    if (!shopId || !form.categoryId) {
      setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      setSkuPkgCapsLoading(false);
      setCategoryStandard(null);
      return undefined;
    }
    const categoryId = String(form.categoryId);
    let cancelled = false;
    setSkuPkgCapsLoading(true);
    setCategoryStandard(null);
    sheinPlatformAPI.getPublishFillInStandard(shopId, {
      category_id: Number(categoryId),
    })
      .then((res) => {
        if (cancelled) return;
        const nextCaps = resolveSkuPackageCapabilities(res);
        setSkuPkgCaps(nextCaps);
        setCategoryStandard(res);
        // 规范币种必须按末级类目取：店铺级（不带 category_id）常拿不到 currency，
        // 会让非服装类目报「未从商品发布规范解析到可用币种 / 缺少供货价币种」。
        if (!shopCaps.usesPriceInfo) {
          const catCurrencies = extractPublishCurrencies(res);
          if (catCurrencies.length) {
            setPublishCurrencies(catCurrencies);
          } else {
            // 类目规范没带 currency 的兜底：再试一次店铺级规范
            sheinPlatformAPI.getPublishFillInStandard(shopId, {})
              .then((shopStd) => {
                if (cancelled || !shopStd) return;
                const shopCurrencies = extractPublishCurrencies(shopStd);
                if (shopCurrencies.length) setPublishCurrencies(shopCurrencies);
              })
              .catch(() => {});
          }
        }
        // 能力关闭时清空残留，避免脏数据进预检 / 误展示
        if (!nextCaps.quantityInfo.show || !nextCaps.packageType.show) {
          setForm((prev) => {
            let changed = false;
            const skcList = (prev.skcList || []).map((skc) => {
              let skcChanged = false;
              const skuRows = (skc.skuRows || []).map((sku) => {
                const patch = {};
                if (!nextCaps.quantityInfo.show) {
                  if (sku.quantity != null && String(sku.quantity).trim() !== '') {
                    patch.quantity = '';
                  }
                  if (sku.quantityUnit != null && String(sku.quantityUnit) !== '1') {
                    patch.quantityUnit = '1';
                  }
                }
                if (!nextCaps.packageType.show) {
                  if (sku.packageType != null && String(sku.packageType) !== '' && String(sku.packageType) !== '0') {
                    patch.packageType = '';
                  }
                }
                if (!Object.keys(patch).length) return sku;
                skcChanged = true;
                changed = true;
                return { ...sku, ...patch };
              });
              return skcChanged ? { ...skc, skuRows } : skc;
            });
            return changed ? { ...prev, skcList } : prev;
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSkuPkgCaps(EMPTY_SKU_PACKAGE_CAPS);
      })
      .finally(() => {
        if (!cancelled) setSkuPkgCapsLoading(false);
      });
    return () => { cancelled = true; };
    // shopCaps.usesPriceInfo 仅决定是否用规范币种，不影响规范本身
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, form.categoryId]);

  // 选类目后即查关联必填；已填属性变化时防抖刷新（勿传销售/尺码）
  const productAttrPairsKey = useMemo(
    () => JSON.stringify(collectProductAttributePairs({
      productAttributes: form.productAttributes,
    })),
    [form.productAttributes],
  );
  const linkedAttrCatKeyRef = useRef('');

  useEffect(() => {
    if (!shopId || !form.categoryId || !form.productTypeId) {
      setLinkedAttrRules(new Map());
      setLinkedAttrLoading(false);
      linkedAttrCatKeyRef.current = '';
      return undefined;
    }
    const catKey = `${shopId}|${form.categoryId}|${form.productTypeId}`;
    const categoryChanged = linkedAttrCatKeyRef.current !== catKey;
    if (categoryChanged) {
      linkedAttrCatKeyRef.current = catKey;
      setLinkedAttrRules(new Map());
    }
    const attributeList = collectProductAttributePairs({
      productAttributes: form.productAttributes,
    });
    let cancelled = false;
    setLinkedAttrLoading(true);
    // 类目切换立即查；属性变更防抖，避免连点下拉打爆接口
    const delay = categoryChanged ? 0 : 400;
    const timer = setTimeout(() => {
      sheinPlatformAPI.getAssociatedAttributeRules(shopId, {
        get_linked_rule_req_list: [{
          group_id: '1',
          category_id: Number(form.categoryId),
          product_type_id: Number(form.productTypeId),
          attribute_list: attributeList,
        }],
      })
        .then((res) => {
          if (cancelled) return;
          const nextMap = parseAssociatedAttributeRules(res);
          setLinkedAttrRules(nextMap);
          // 超范围值清空；折叠区内的关联必填自动展开
          setForm((prev) => {
            const attrs = { ...(prev.productAttributes || {}) };
            let changed = false;
            nextMap.forEach((rule, attributeId) => {
              const allowed = rule?.allowedValueIds;
              if (!Array.isArray(allowed) || !allowed.length) return;
              const cur = attrs[attributeId];
              if (cur == null || cur === '') return;
              if (isValueAllowedByLinkedRule(cur, allowed)) return;
              attrs[attributeId] = Array.isArray(cur) ? [] : '';
              changed = true;
            });
            return changed ? { ...prev, productAttributes: attrs } : prev;
          });
        })
        .catch(() => {
          if (!cancelled) setLinkedAttrRules(new Map());
        })
        .finally(() => {
          if (!cancelled) setLinkedAttrLoading(false);
        });
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shopId, form.categoryId, form.productTypeId, productAttrPairsKey]);

  useEffect(() => {
    if (!isEdit || !shopId || (!editSpuName && !initialRecordId && !initialDraftId)) return undefined;
    let cancelled = false;
    setLoadingSpu(true);
    // 审核中商品 searchProduct 往往只有类目；优先本地草稿 form 整表回填
    sheinPlatformAPI.getProductForEdit(shopId, {
      spuName: editSpuName,
      recordId: initialRecordId,
      draftId: initialDraftId,
    })
      .then((res) => {
        if (cancelled) return;
        const info = res || {};
        if (info.draftId != null && info.draftId !== '') {
          setDraftId(String(info.draftId));
        }
        const skcItems = Array.isArray(info.skcItems)
          ? info.skcItems
          : (Array.isArray(info.form?.skcItems) ? info.form.skcItems : []);
        if (info.form && typeof info.form === 'object') {
          const merged = enrichFormWithPlatformSkcIds({
            ...info.form,
            spuName: info.spuName || info.form.spuName || editSpuName,
            title: info.form.title || info.title || '',
            supplierCode: info.form.supplierCode || info.supplierCode || '',
            brandCode: info.form.brandCode || info.brandCode || '',
            categoryId: String(info.form.categoryId || info.categoryId || ''),
            productTypeId: String(info.form.productTypeId || info.productTypeId || ''),
            categoryPathLabel: info.form.categoryPathLabel || info.categoryPathLabel || '',
            description: info.form.description || info.description || '',
            originCountry: info.form.originCountry || info.originCountry || '',
          }, skcItems);
          const nextForm = sanitizeSheinFormImageSlots(
            withDefaultWarehouse(emptyForm(merged), meta.warehouses),
          );
          editBaselineRef.current = JSON.parse(JSON.stringify(nextForm));
          setForm(nextForm);
          if (info.platformLoadWarning) {
            showToast(String(info.platformLoadWarning), 'warning');
          }
          return;
        }
        const flatForm = sanitizeSheinFormImageSlots(withDefaultWarehouse(enrichFormWithPlatformSkcIds({
          ...emptyForm({}),
          spuName: info.spuName || editSpuName || '',
          title: info.title || '',
          titleLanguage: info.titleLanguage || 'en',
          description: info.description || '',
          supplierCode: info.supplierCode || '',
          brandCode: info.brandCode || '',
          originCountry: info.originCountry || '',
          categoryId: info.categoryId != null ? String(info.categoryId) : '',
          productTypeId: info.productTypeId != null ? String(info.productTypeId) : '',
          categoryPathLabel: info.categoryPathLabel || '',
        }, skcItems), meta.warehouses));
        editBaselineRef.current = JSON.parse(JSON.stringify(flatForm));
        setForm(flatForm);
      })
      .catch((e) => {
        if (!cancelled) showToast(e?.message || t('publishFlow.detailLoadFail'), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoadingSpu(false);
      });
    return () => { cancelled = true; };
  }, [isEdit, shopId, editSpuName, initialRecordId, initialDraftId, t]);

  useEffect(() => {
    if (!colorAttr && !sizeAttr) return;
    setForm((prev) => {
      const colorValues = colorAttr?.values || [];
      const sizeValues = sizeAttr?.values || [];
      const colorIds = new Set(colorValues.map((v) => String(v.id)));
      const sizeIds = new Set(sizeValues.map((v) => String(v.id)));
      const defaults = {
        price: prev.supplyPrice || '',
        stock: prev.stockQty || '',
        weight: prev.weightGrams || '',
        currency: prev.currency || '',
      };
      // 尺码表纵轴以 SKU 规格为准，禁止合并模板残留的 selectedSizeIds（否则会出现两个 mid-platform）
      const rowSizeIds = deriveSelectedSizeIds(prev.skcList || []).map(String);
      const prevSizeIds = (prev.selectedSizeIds || []).map(String);
      const nextSizeIds = rowSizeIds.length
        ? rowSizeIds
        : prevSizeIds.filter((id) => !isCustomSpecKey(id) && (!sizeValues.length || sizeIds.has(id)));
      const nextSkcList = (prev.skcList || []).map((skc) => {
        const knownColor = !skc.colorValueId || colorIds.has(String(skc.colorValueId));
        const asCustom = Boolean(
          skc.colorIsCustom
          || skc.colorExtraValue
          || (colorValues.length > 0 && skc.colorValueId && !knownColor),
        );
        const colorLabel = asCustom
          ? (skc.colorExtraValue || skc.colorLabel || '')
          : (colorAttr?.values?.find((v) => String(v.id) === String(skc.colorValueId))?.name
            || skc.colorLabel);
        const existingRows = skc.skuRows || [];
        // 编辑多 SKU：保留全部行；仅在新建且用户主动勾选尺码时按 sizeIds 重建
        const keepAllRows = isEdit || existingRows.length > 1 || !nextSizeIds.length;
        return {
          ...skc,
          colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
          colorIsCustom: asCustom,
          colorExtraValue: asCustom
            ? (skc.colorExtraValue || skc.colorLabel || '')
            : (skc.colorExtraValue || ''),
          colorLabel,
          skuRows: keepAllRows
            ? existingRows.map((row) => ({
              ...row,
              sizeAttributeId: row.sizeAttributeId || sizeAttr?.attributeId || '',
            }))
            : buildSkuRowsForSizes(existingRows, nextSizeIds, sizeAttr, defaults),
        };
      }).filter((skc) => (
        skc.colorValueId
        || String(skc.colorExtraValue || '').trim()
        || String(skc.colorLabel || '').trim()
        || (skc.skuRows || []).length > 0
      ));
      const nextState = {
        ...prev,
        sizeSaleAttributeId: prev.sizeSaleAttributeId || sizeAttr?.attributeId || '',
        selectedSizeIds: nextSizeIds.length ? nextSizeIds : prev.selectedSizeIds,
        skcList: nextSkcList,
      };
      const sameSizes = JSON.stringify((prev.selectedSizeIds || []).map(String))
        === JSON.stringify((nextState.selectedSizeIds || []).map(String));
      const sameSaleAttr = String(prev.sizeSaleAttributeId || '') === String(nextState.sizeSaleAttributeId || '');
      const sameSkc = prev.skcList === nextSkcList || (
        (prev.skcList || []).length === nextSkcList.length
        && (prev.skcList || []).every((skc, i) => {
          const n = nextSkcList[i];
          return skc === n
            || (
              String(skc.colorAttributeId || '') === String(n.colorAttributeId || '')
              && Boolean(skc.colorIsCustom) === Boolean(n.colorIsCustom)
              && String(skc.colorExtraValue || '') === String(n.colorExtraValue || '')
              && String(skc.colorLabel || '') === String(n.colorLabel || '')
              && (skc.skuRows || []).length === (n.skuRows || []).length
              && (skc.skuRows || []).every((row, j) => (
                String(row.sizeAttributeId || '') === String(n.skuRows[j]?.sizeAttributeId || '')
              ))
            );
        })
      );
      if (sameSizes && sameSaleAttr && sameSkc) return prev;
      return nextState;
    });
  }, [colorAttr?.attributeId, sizeAttr?.attributeId, isEdit]);

  useEffect(() => {
    if (!form.categoryId || !categoryOptions.length) return;
    const selected = categoryOptions.find((item) => String(item.categoryId) === String(form.categoryId));
    if (!selected) return;
    const selectedType = normalizePublishProductTypeId(selected.productTypeId);
    const needType = !normalizePublishProductTypeId(form.productTypeId) && selectedType;
    const needPath = !form.categoryPathLabel && selected.label;
    if (!needType && !needPath) return;
    setForm((prev) => {
      if (String(prev.categoryId) !== String(selected.categoryId)) return prev;
      return {
        ...prev,
        productTypeId: normalizePublishProductTypeId(prev.productTypeId) || selectedType,
        categoryPathLabel: prev.categoryPathLabel || selected.label || '',
      };
    });
  }, [form.categoryId, form.productTypeId, form.categoryPathLabel, categoryOptions]);

  const applyProductTemplateById = async (templateId, {
    categoryId = '',
    productTypeId = '',
    categoryPathLabel = '',
    scope = '',
    silent = false,
  } = {}) => {
    const id = String(templateId || '').trim();
    if (!shopId || !id) return false;
    const query = {};
    const scopeKey = String(scope || '').trim().toUpperCase();
    if (scopeKey) query.scope = scopeKey;
    const res = await sheinPlatformAPI.getProductTemplate(shopId, id, query);
    const item = res?.item || res || {};
    const rawPayload = item.payload && typeof item.payload === 'object' ? item.payload : {};
    const formData = rawPayload.form && typeof rawPayload.form === 'object'
      ? rawPayload.form
      : rawPayload;
    setForm(sanitizeSheinFormImageSlots(emptyForm({
      ...formData,
      categoryId: categoryId || formData.categoryId || item.categoryId || '',
      productTypeId: productTypeId || formData.productTypeId || item.productTypeId || '',
      categoryPathLabel: categoryPathLabel || formData.categoryPathLabel || item.categoryPathLabel || '',
    })));
    setSelectedAttrTemplateId(String(
      rawPayload.selectedAttrTemplateId || formData.selectedAttrTemplateId || '',
    ).trim());
    setSelectedSizeChartTemplateId(String(
      rawPayload.selectedSizeChartTemplateId || formData.selectedSizeChartTemplateId || '',
    ).trim());
    setSelectedProductTemplateId(String(item.id || id));
    setSelectedProductTemplateVersion(parseTemplateVersion(item.version));
    setSelectedAttrTemplateVersion(null);
    setSelectedSizeChartTemplateVersion(null);
    if (!silent) {
      showToast(t('publishFlow.appliedTpl', { name: item.name || id }), 'success');
    }
    return true;
  };

  // 选类目后加载发品可用产品模板（个人 + 团队）；用户主动切换类目时自动套用优先级最高的一项
  useEffect(() => {
    if (isEdit || !shopId || !normalizePublishProductTypeId(form.productTypeId)) {
      if (!normalizePublishProductTypeId(form.productTypeId)) {
        setProductTemplates([]);
      }
      return undefined;
    }
    const productTypeId = normalizePublishProductTypeId(form.productTypeId);
    const categoryId = String(form.categoryId || '');
    const categoryPathLabel = String(form.categoryPathLabel || '');
    const shouldAuto = String(pendingProductTplAutoForRef.current || '') === productTypeId;
    pendingProductTplAutoForRef.current = '';
    const seq = categorySelectSeqRef.current + 1;
    categorySelectSeqRef.current = seq;
    setProductTemplateBusy(true);
    let cancelled = false;
    loadPublishProductTemplates(shopId, productTypeId)
      .then(async (sorted) => {
        if (cancelled || categorySelectSeqRef.current !== seq) return;
        setProductTemplates(sorted);
        if (!shouldAuto) return;
        const picked = pickHighestPriorityTemplate(sorted);
        if (!picked?.id) return;
        // 产品模板已带类目属性/尺码库选中项，跳过属性库自动套用，避免覆盖
        pendingAttrAutoForRef.current = '';
        try {
          await applyProductTemplateById(picked.id, {
            categoryId,
            productTypeId,
            categoryPathLabel,
            scope: picked.scope,
          });
        } catch (e) {
          showToast(e?.message || t('publishFlow.autoApplyFail'), 'error');
        }
      })
      .catch(() => {
        if (cancelled || categorySelectSeqRef.current !== seq) return;
        setProductTemplates([]);
      })
      .finally(() => {
        if (!cancelled && categorySelectSeqRef.current === seq) {
          setProductTemplateBusy(false);
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅随类目类型变化加载/自动套用
  }, [isEdit, shopId, form.productTypeId, form.categoryId]);

  const onCategoryChange = (categoryId) => {
    const nextCategoryId = categoryId == null || categoryId === '' ? '' : String(categoryId);
    // 再点一次当前类目不算切换：否则会清空模板带入的属性/规格，且自动套用不会重跑
    if (String(form.categoryId || '') === nextCategoryId) {
      return;
    }
    const selected = categoryOptions.find((item) => String(item.categoryId) === nextCategoryId);
    const nextProductTypeId = normalizePublishProductTypeId(selected?.productTypeId);
    const nextCategoryPathLabel = selected?.label || '';
    pendingCategoryPreserveRef.current = {
      productAttributes: pickFilledProductAttributes(form.productAttributes),
      sizeChart: form.sizeChart && typeof form.sizeChart === 'object' ? form.sizeChart : {},
      selectedSizeIds: Array.isArray(form.selectedSizeIds) ? [...form.selectedSizeIds] : [],
      skcList: Array.isArray(form.skcList) ? form.skcList : [],
      prevAttrTpl: attrTplRef.current,
    };
    pendingAttrAutoForRef.current = nextProductTypeId;
    pendingProductTplAutoForRef.current = nextProductTypeId;
    setSelectedAttrTemplateId('');
    setSelectedSizeChartTemplateId('');
    setSelectedProductTemplateId('');
    setSelectedProductTemplateVersion(null);
    setSelectedAttrTemplateVersion(null);
    setSelectedSizeChartTemplateVersion(null);
    setVersionConflicts({});
    setProductTemplates([]);
    setForm((prev) => ({
      ...prev,
      categoryId: nextCategoryId,
      productTypeId: nextProductTypeId,
      categoryPathLabel: nextCategoryPathLabel,
      productAttributes: {},
      sizeChart: {},
      skcList: [],
      selectedSizeIds: [],
    }));
    setAttrsExpanded(false);
  };

  const onProductTemplateSelect = async (templateId) => {
    const id = String(templateId || '').trim();
    setSelectedProductTemplateId(id);
    if (!id) return;
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const hit = productTemplates.find((item) => String(item?.id) === id);
    setProductTemplateBusy(true);
    try {
      pendingAttrAutoForRef.current = '';
      await applyProductTemplateById(id, {
        categoryId: form.categoryId,
        productTypeId: form.productTypeId,
        categoryPathLabel: form.categoryPathLabel,
        scope: hit?.scope,
      });
    } catch (e) {
      showToast(e?.message || t('publishFlow.applyFail'), 'error');
    } finally {
      setProductTemplateBusy(false);
    }
  };

  const onIpChange = (ipId) => {
    const selected = ipOptions.find((item) => item.ipId === ipId);
    setForm((prev) => ({
      ...prev,
      ipId: selected?.ipId || '',
      ipName: selected?.ipName || '',
      ipNameCn: selected?.ipNameCn || '',
    }));
  };

  const onSitesChange = (abbrs) => {
    const selected = [...new Set((abbrs || []).map(String).filter(Boolean))];
    const first = siteOptions.find((s) => selected.includes(s.siteAbbr));
    setForm((prev) => ({
      ...prev,
      selectedSites: selected,
      mainSite: first?.mainSiteCode || prev.mainSite || 'shein',
      // 自运营币种随已选站点由 pickSiteListCurrency 回写；半托管/全托管仍走发布规范
    }));
  };

  const onWarehouseChange = (warehouseId) => {
    const selected = warehouseOptions.find((item) => String(item.value) === String(warehouseId));
    setForm((prev) => ({
      ...prev,
      warehouseId: selected?.value || '',
      warehouseName: selected?.name || '',
    }));
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    document.getElementById(`spo-pub-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToFieldError = (field, section) => {
    if (section) scrollToSection(section);
    const el = document.querySelector(`[data-field-error="${field}"]`);
    if (el) {
      window.setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
    }
  };

  const applyFieldErrors = (errors, fallbackMessage = '') => {
    const list = Array.isArray(errors) ? errors : [];
    const map = fieldErrorsToMap(list);
    setFieldErrors(map);
    const first = list[0];
    if (first?.field) {
      scrollToFieldError(first.field, first.section);
    }
    const tip = first?.message || fallbackMessage;
    if (tip) showToast(tip, 'error', Math.min(12000, Math.max(4000, String(tip).length * 50)));
  };

  const buildPublishFormSnapshot = (formSource = form) => {
    const sizeValueLabels = {};
    (sizeAttr?.values || []).forEach((v) => {
      if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
    });
    const skcList = (formSource.skcList || []).map((skc) => ({
      ...skc,
      skuRows: (skc.skuRows || []).map((sku) => ({
        ...sku,
        sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
          || sku.sizeLabel
          || '',
      })),
    }));
    const selectedSizeIds = (formSource.selectedSizeIds?.length
      ? formSource.selectedSizeIds
      : deriveSelectedSizeIds(skcList)
    ).map((id) => String(id));
    return {
      ...formSource,
      skcList,
      selectedSizeIds,
      sizeValueLabels,
      selectedAttrTemplateId: String(selectedAttrTemplateId || '').trim(),
      selectedSizeChartTemplateId: String(selectedSizeChartTemplateId || '').trim(),
      selectedProductTemplateId: String(selectedProductTemplateId || '').trim(),
    };
  };

  const onSaveDraft = async () => {
    if (!shopId) {
      showToast(t('publishFlow.pickShopDraft'), 'warning');
      return;
    }
    setSavingDraft(true);
    try {
      const enriched = await enrichSheinFormPrintNames(form);
      const formWithPrint = applyPrintNamesToSheinForm(enriched);
      if (formWithPrint !== form) setForm(formWithPrint);
      const payload = {
        form: persistSheinFormImageSlots(buildPublishFormSnapshot(formWithPrint)),
      };
      const res = draftId
        ? await sheinPlatformAPI.updatePublishDraft(shopId, draftId, payload)
        : await sheinPlatformAPI.createPublishDraft(shopId, payload);
      if (res?.id) setDraftId(String(res.id));
      showToast(t('publishFlow.draftSaved'), 'success');
      allowUnsavedLeave();
      navigate(buildSheinPath('products/drafts'));
    } catch (e) {
      showToast(e?.message || t('publishFlow.draftFail'), 'error');
    } finally {
      setSavingDraft(false);
    }
  };

  const openProductTplSaveModal = () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    if (!form.categoryId || !form.productTypeId) {
      showToast(t('publishAttr.pickCategory'), 'error');
      return;
    }
    setProductTplSaveModalOpen(true);
  };

  const showSkuQuantity = !skuPkgCapsLoading && skuPkgCaps.quantityInfo.show === true;
  const showSkuPackageType = !skuPkgCapsLoading && skuPkgCaps.packageType.show === true;
  const requireSkuQuantity = showSkuQuantity && skuPkgCaps.quantityInfo.required === true;
  const requireSkuPackageType = showSkuPackageType && skuPkgCaps.packageType.required === true;

  /** 建议零售价（SPU 级 suggested_retail_price）：非服装类目常见必填，服装类目通常不展示 */
  const suggestPriceRule = useMemo(() => {
    if (!categoryStandard) return { show: false, required: false };
    const rule = getFieldRule(parseFillInStandardMap(categoryStandard), 'suggest_price');
    return { show: rule.show, required: rule.required };
  }, [categoryStandard]);

  // 建议零售价：规范不允许时清空残留；允许时币种跟随规范币种，
  // 价格优先沿用已填的 SKU 建议零售价（RRP），避免同一份数据让用户填两遍。
  useEffect(() => {
    setForm((prev) => {
      if (!suggestPriceRule.show) {
        if (!prev.suggestedRetailPrice && !prev.suggestedRetailPriceCurrency) return prev;
        return { ...prev, suggestedRetailPrice: '', suggestedRetailPriceCurrency: '' };
      }
      let price = String(prev.suggestedRetailPrice ?? '').trim();
      let currency = String(prev.suggestedRetailPriceCurrency ?? '').trim();
      if (!price) {
        const fromRrp = (prev.skcList || [])
          .flatMap((skc) => (Array.isArray(skc.skuRows) ? skc.skuRows : []))
          .flatMap((sku) => (Array.isArray(sku.siteRrpInfoList) ? sku.siteRrpInfoList : []))
          .find((row) => String(row?.price ?? '').trim());
        if (fromRrp) {
          price = String(fromRrp.price).trim();
          const rrpCurrency = String(fromRrp.currency || '').trim();
          if (rrpCurrency) currency = rrpCurrency;
        }
      }
      if (!currency) currency = String(platformCurrency || prev.currency || '').trim().toUpperCase();
      if (price === String(prev.suggestedRetailPrice ?? '')
        && currency === String(prev.suggestedRetailPriceCurrency ?? '')) {
        return prev;
      }
      return { ...prev, suggestedRetailPrice: price, suggestedRetailPriceCurrency: currency };
    });
  }, [suggestPriceRule.show, platformCurrency]);

  const isLinkedRequired = (attributeId) => linkedAttrRules.has(String(attributeId || ''));
  const linkedAllowedIds = (attributeId) => {
    const rule = linkedAttrRules.get(String(attributeId || ''));
    return Array.isArray(rule?.allowedValueIds) ? rule.allowedValueIds : null;
  };

  const { requiredAttrs, optionalAttrs } = useMemo(
    () => partitionProductAttrsForDisplay(attrTpl, linkedAttrRules),
    [attrTpl, linkedAttrRules],
  );
  const allProductAttrs = useMemo(
    () => [...requiredAttrs, ...optionalAttrs],
    [requiredAttrs, optionalAttrs],
  );

  const handleAiTitle = async () => {
    if (aiTitleBusy || submitting) return;
    const confirmed = await confirmIpeCreditAction({
      navId: 'aiCopywriting',
      imageCount: 1,
      title: t('templateEditor.aiTitleDialog'),
      confirmLabel: t('templateEditor.confirmGen'),
    });
    if (!confirmed) return;

    setAiTitleBusy(true);
    showToast(t('templateEditor.aiTitleBusyShein'), 'info');
    try {
      const title = await generateSheinPublishAiTitle({
        skcList: form.skcList,
        categoryPathLabel: form.categoryPathLabel,
        productAttributes: form.productAttributes,
        attrDefs: allProductAttrs,
      });
      setForm((prev) => ({
        ...prev,
        title: title.slice(0, 150),
        titleLanguage: 'en',
      }));
      showToast(t('templateEditor.aiTitleDoneShein'), 'success');
    } catch (error) {
      showToast(error?.message || t('templateEditor.aiTitleFail'), 'error');
    } finally {
      setAiTitleBusy(false);
    }
  };

  const handleAssessPrintInfringement = async () => {
    if (printInfringementBusy || form.ipePrintInfringement?.assessing) return;
    const imageUrl = resolveFirstPrintImageUrl(form.printImages);
    if (!imageUrl) {
      showToast(t('publishFlow.needPrint'), 'warning');
      return;
    }
    const confirmed = await confirmIpeCreditAction({
      navId: 'infringementDetection',
      imageCount: 1,
      title: t('publishSpec.infringement'),
      confirmLabel: t('publishFlow.confirmDetect'),
    });
    if (!confirmed) return;

    setPrintInfringementBusy(true);
    setForm((prev) => ({
      ...prev,
      ipePrintInfringement: {
        ...normalizeSheinPrintInfringement(prev.ipePrintInfringement),
        assessing: true,
      },
    }));
    showToast(t('publishFlow.printBusy'), 'info');
    try {
      const result = await assessSheinPublishPrintInfringement(imageUrl);
      setForm((prev) => ({
        ...prev,
        ipePrintInfringement: normalizeSheinPrintInfringement({
          risk: result.risk,
          assessing: false,
          riskAdvice: result.riskAdvice,
          assessedAt: result.assessedAt,
        }),
      }));
      showToast(t('publishFlow.printDone'), 'success');
    } catch (error) {
      setForm((prev) => ({
        ...prev,
        ipePrintInfringement: normalizeSheinPrintInfringement({
          risk: 'pending',
          assessing: false,
          riskAdvice: error?.message || t('publishFlow.printFail'),
          assessedAt: new Date().toISOString(),
        }),
      }));
      showToast(error?.message || t('publishFlow.printFail'), 'error');
    } finally {
      setPrintInfringementBusy(false);
    }
  };

  const validate = (targetForm = form) => validateSheinPublishForm(targetForm, {
    isEdit,
    shopId,
    shopType: activeShop?.shopType,
    attrTpl,
    colorAttr,
    sizeAttr,
    sizeMeasureAttrs,
    warehouseOptions: meta.warehouses || [],
    skuPkgCaps,
    linkedAttrRules,
  });

  const clearVersionConflict = (type) => {
    const key = conflictFieldKey(type);
    setVersionConflicts((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setChangePreviews((prev) => {
      if (!prev?.[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const buildAttrChangePreview = async () => {
    const id = String(selectedAttrTemplateId || '').trim();
    if (!shopId || !id) return { title: t('templateEditor.attrUpdated'), changes: [] };
    const res = await sheinPlatformAPI.getProductAttributeTemplate(shopId, id);
    const item = res?.item || {};
    const { patch } = applyProductAttributeTemplatePayload(
      item.payload || {},
      (attrTplRef.current?.requiredProduct || [])
        .concat(attrTplRef.current?.mainAttrs || [])
        .concat(attrTplRef.current?.otherAttrs || [])
        .map((attr) => String(attr.attributeId || ''))
        .filter(Boolean),
    );
    const nextAttrs = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current || attrTpl);
    const attrDefs = [
      ...(attrTplRef.current?.requiredProduct || attrTpl?.requiredProduct || []),
      ...(attrTplRef.current?.mainAttrs || attrTpl?.mainAttrs || []),
      ...(attrTplRef.current?.otherAttrs || attrTpl?.otherAttrs || []),
    ];
    const { changes, count } = diffProductAttributeChanges({
      current: form.productAttributes || {},
      next: nextAttrs,
      attrDefs,
    });
    return {
      title: t('templateEditor.attrUpdated'),
      intro: t('templateEditor.attrDiffIntro', { name: item.name || id, count }),
      changes,
      count,
    };
  };

  const buildSizeChartChangePreview = async () => {
    const id = String(selectedSizeChartTemplateId || '').trim();
    if (!shopId || !id) return { title: t('templateEditor.sizeUpdated'), changes: [] };
    const res = await sheinPlatformAPI.getSizeChartTemplate(shopId, id);
    const item = res?.item || {};
    const patch = extractSizeChartTemplatePatch(item);
    const { changes, count } = diffSizeChartChanges({
      current: form.sizeChart || {},
      next: patch.sizeChart || {},
      measureAttrs: sizeMeasureAttrs,
    });
    return {
      title: t('templateEditor.sizeUpdated'),
      intro: t('templateEditor.sizeDiffIntro', { name: item.name || id, count }),
      changes,
      count,
    };
  };

  const reloadConflictingTemplateRegion = async (type) => {
    if (!shopId) return;
    setReloadingConflictType(type);
    try {
      if (type === TEMPLATE_VERSION_TYPES.product) {
        const id = String(selectedProductTemplateId || '').trim();
        if (!id) return;
        const res = await sheinPlatformAPI.getProductTemplate(shopId, id);
        const item = res?.item || res || {};
        const merged = mergeProductTemplateIntoForm(form, item);
        setForm((prev) => mergeProductTemplateIntoForm(prev, item).form);
        if (merged.selectedAttrTemplateId) {
          setSelectedAttrTemplateId(merged.selectedAttrTemplateId);
        }
        if (merged.selectedSizeChartTemplateId) {
          setSelectedSizeChartTemplateId(merged.selectedSizeChartTemplateId);
        }
        setSelectedProductTemplateVersion(merged.version);
        clearVersionConflict(type);
        showToast(t('publishFlow.reloadedTpl'), 'success');
        return;
      }
      if (type === TEMPLATE_VERSION_TYPES.attribute) {
        const id = String(selectedAttrTemplateId || '').trim();
        if (!id) return;
        const res = await sheinPlatformAPI.getProductAttributeTemplate(shopId, id);
        const patch = extractAttrTemplatePatch(res?.item || res || {});
        setForm((prev) => {
          const aligned = alignAttributesToCategoryTemplate(
            { ...(patch.productAttributes || {}) },
            attrTplRef.current || attrTpl,
          );
          return { ...prev, productAttributes: aligned };
        });
        setSelectedAttrTemplateVersion(patch.version);
        clearVersionConflict(type);
        showToast(t('publishFlow.reloadedAttrs'), 'success');
        return;
      }
      if (type === TEMPLATE_VERSION_TYPES.sizeChart) {
        const id = String(selectedSizeChartTemplateId || '').trim();
        if (!id) return;
        const res = await sheinPlatformAPI.getSizeChartTemplate(shopId, id);
        const patch = extractSizeChartTemplatePatch(res?.item || res || {});
        applySizeChart(patch.sizeChart || {}, { replace: true });
        setSelectedSizeChartTemplateVersion(patch.version);
        clearVersionConflict(type);
        showToast(t('publishFlow.reloadedSize'), 'success');
      }
    } catch (e) {
      if (isMissingTemplateError(e)
        && (type === TEMPLATE_VERSION_TYPES.attribute || type === TEMPLATE_VERSION_TYPES.sizeChart)) {
        if (type === TEMPLATE_VERSION_TYPES.attribute) {
          setSelectedAttrTemplateId('');
          setSelectedAttrTemplateVersion(null);
        } else {
          setSelectedSizeChartTemplateId('');
          setSelectedSizeChartTemplateVersion(null);
        }
        clearVersionConflict(type);
        showToast(
          type === TEMPLATE_VERSION_TYPES.sizeChart
            ? t('publishFlow.refSizeOut')
            : t('publishFlow.refAttrOut'),
          'warning',
        );
        return;
      }
      showToast(e?.message || t('templateEditor.reloadFail'), 'error');
    } finally {
      setReloadingConflictType('');
    }
  };

  const onSubmit = async () => {
    const enriched = await enrichSheinFormPrintNames(form);
    const formWithPrintNames = applyPrintNamesToSheinForm(enriched);
    if (formWithPrintNames !== form) {
      setForm(formWithPrintNames);
    }
    const result = validate(formWithPrintNames);
    if (!result.ok) {
      applyFieldErrors([{
        field: result.field || 'form',
        section: result.section || 'basic',
        message: result.message,
      }]);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      // 新建发品 / 部分编辑：均按平台规范做发品前核验
      setSubmitHint(isEdit
        ? t('publishFlow.checkingSku')
        : t('publishFlow.checkingSku'));
      const preflight = await runSheinPublishPreflight({
        shopId,
        form: formWithPrintNames,
        isEdit,
        attrTpl,
        shopType: activeShop?.shopType,
        shopCaps,
        siteList: meta.sites,
      });
      if (!preflight.ok) {
        applyFieldErrors([{
          field: preflight.field || 'form',
          section: preflight.section || 'basic',
          message: preflight.message,
        }]);
        showToast(preflight.message, 'error');
        return;
      }
      publishStandardRef.current = preflight.standardRaw || null;

      // 新建发品：按品牌 RRP 规则校验建议零售价
      if (!isEdit) {
        const rrpCheck = validatePublishRrp(form, rrpRule);
        if (!rrpCheck.ok) {
          applyFieldErrors([{
            field: rrpCheck.field || 'rrp',
            section: rrpCheck.section || 'spec',
            message: rrpCheck.message,
          }]);
          showToast(rrpCheck.message, 'error');
          return;
        }
      }

      const templateRefs = collectAppliedTemplateRefs({
        productTemplateId: selectedProductTemplateId,
        productTemplateVersion: selectedProductTemplateVersion,
        attrTemplateId: selectedAttrTemplateId,
        attrTemplateVersion: selectedAttrTemplateVersion,
        sizeChartTemplateId: selectedSizeChartTemplateId,
        sizeChartTemplateVersion: selectedSizeChartTemplateVersion,
      });
      if (templateRefs.length > 0) {
        setSubmitHint(t('publishFlow.checkingTemplates'));
        const versionCheck = await checkTemplateVersions(sheinPlatformAPI, shopId, templateRefs);
        if (!versionCheck.ok) {
          const conflicts = versionCheck.conflicts || [];
          const { missingLibraryRefs, blocking } = partitionTemplateVersionConflicts(conflicts);
          if (missingLibraryRefs.length) {
            missingLibraryRefs.forEach((c) => {
              if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                setSelectedAttrTemplateId('');
                setSelectedAttrTemplateVersion(null);
              } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                setSelectedSizeChartTemplateId('');
                setSelectedSizeChartTemplateVersion(null);
              }
            });
          }
          if (blocking.length) {
          const nextMarks = {};
          blocking.forEach((c) => {
            const key = conflictFieldKey(c.type);
            if (key) nextMarks[key] = true;
          });
          setVersionConflicts(nextMarks);
          const nextPreviews = {};
          await Promise.all(blocking.map(async (c) => {
            try {
              if (c.type === TEMPLATE_VERSION_TYPES.attribute) {
                nextPreviews.productAttributes = await buildAttrChangePreview();
              } else if (c.type === TEMPLATE_VERSION_TYPES.sizeChart) {
                nextPreviews.sizeChart = await buildSizeChartChangePreview();
              }
            } catch {
              /* ignore */
            }
          }));
          setChangePreviews(nextPreviews);
          const attrCount = nextPreviews.productAttributes?.count;
          const sizeCount = nextPreviews.sizeChart?.count;
          const detailBits = [];
          if (attrCount != null) detailBits.push(t('templateEditor.attrCount', { count: attrCount }));
          if (sizeCount != null) detailBits.push(t('templateEditor.sizeCount', { count: sizeCount }));
          showToast(
            detailBits.length
              ? `${conflictToastMessage(blocking)}${t('templateEditor.conflictDetail', { detail: detailBits.join('，') })}`
              : conflictToastMessage(blocking),
            'warning',
            3000,
          );
          const firstField = conflictFieldKey(blocking[0]?.type) || 'form';
          const el = document.querySelector(`[data-field-error="${firstField}"]`);
          if (el?.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
          }
        }
        setVersionConflicts({});
        setChangePreviews({});
      }

      // 提交币种：半托管/全托管对齐发布规范；自运营对齐站点列表（已写入 publishCurrencies）
      const alignedCurrency = pickPublishCurrency(
        publishCurrencies,
        formWithPrintNames.currency || platformCurrency,
      );
      if (!alignedCurrency) {
        applyFieldErrors([{
          field: 'currency',
          section: 'sales',
          message: shopCaps.usesPriceInfo
            ? t('publishFlow.needSiteCurrency')
            : t('publishFlow.needRuleCurrency'),
        }]);
        return;
      }
      const sizeValueLabels = { ...(formWithPrintNames.sizeValueLabels || {}) };
      (sizeAttr?.values || []).forEach((v) => {
        if (v?.id != null && v?.name) sizeValueLabels[String(v.id)] = String(v.name);
      });
      const resolvedSizeSaleAttrId = formWithPrintNames.sizeSaleAttributeId || sizeAttr?.attributeId || '';
      // 提交纵轴必须与供应表 SKU 一致，勿用可能含重复行的 selectedSizeIds
      const fromSkcSizes = deriveSelectedSizeIds(formWithPrintNames.skcList).map((id) => String(id));
      const resolvedSelectedSizeIds = fromSkcSizes.length
        ? fromSkcSizes
        : (formWithPrintNames.selectedSizeIds || []).map((id) => String(id));
      const submitFormBase = {
        ...formWithPrintNames,
        sizeValueLabels,
        // 尺码表关联次销售：缺 id 时平台会报「尺寸表填写数值不能为空 / 单选多值」
        sizeSaleAttributeId: resolvedSizeSaleAttrId,
        selectedSizeIds: resolvedSelectedSizeIds,
        // 从已发布商品另存草稿再发：按新建品处理，勿带平台 SPU
        spuName: isEdit ? formWithPrintNames.spuName : '',
        currency: alignedCurrency || formWithPrintNames.currency,
        skcList: (formWithPrintNames.skcList || []).map((skc) => {
          const resolved = resolveSkcColorAgainstTemplate({
            ...skc,
            colorAttributeId: skc.colorAttributeId || colorAttr?.attributeId || '',
          }, colorAttr);
          return {
          ...resolved,
          // 确保主规格属性 ID 齐全，否则 sale_attribute 组不出来
          colorLabel: resolved.colorIsCustom || resolved.colorExtraValue
            ? (resolved.colorExtraValue || resolved.colorLabel)
            : (colorAttr?.values?.find((v) => String(v.id) === String(resolved.colorValueId))?.name
              || resolved.colorLabel),
          skcName: isEdit ? skc.skcName : '',
          skuRows: (skc.skuRows || []).map((sku) => ({
            ...sku,
            sizeAttributeId: sku.sizeAttributeId || sizeAttr?.attributeId || resolvedSizeSaleAttrId || '',
            sizeLabel: sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))?.name
              || sku.sizeLabel
              || sizeValueLabels[String(sku.sizeValueId || '')]
              || '',
            currency: alignedCurrency || sku.currency || formWithPrintNames.currency,
            skuCode: isEdit ? sku.skuCode : '',
          })),
        };
        }),
      };
      // 丢掉模板残留规格行；mode=3（如周长）收敛为单值，避免平台报「单选多值」
      const submitForm = {
        ...submitFormBase,
        sizeChart: pruneSizeChartForSubmit(submitFormBase, sizeMeasureAttrs),
        shopType: activeShop?.shopType,
        siteList: meta.sites,
      };
      if (isEdit) {
        const idCheck = validateEditPlatformIds(submitForm, editBaselineRef.current);
        if (!idCheck.ok) {
          applyFieldErrors([{
            field: 'skcList',
            section: 'spec',
            message: idCheck.message,
          }]);
          return;
        }
        const priceChanges = collectSkuPriceChanges(submitForm, editBaselineRef.current, shopCaps);
        if (!priceChanges.empty && priceChanges.mode === 'none') {
          applyFieldErrors([{
            field: 'supply',
            section: 'spec',
            message: t('publishFlow.priceUnsupported'),
          }]);
          return;
        }
        // 成本价任意变更、售价涨价：先确认涨价原因
        if (!priceChanges.empty && (priceChanges.mode === 'cost' || priceChanges.hasIncrease)) {
          setPriceConfirm({ submitForm, changes: priceChanges });
          return;
        }
        await finishEditSubmit(submitForm, priceChanges, {});
        return;
      }

      let payload;
      try {
        payload = buildSheinPublishPayload(submitForm, {
          isEdit: false,
          sizeAttrs: sizeMeasureAttrs,
          skuPkgCaps,
          shopType: activeShop?.shopType,
          siteList: meta.sites,
          attrTpl,
          standardRaw: publishStandardRef.current || categoryStandard,
        });
      } catch (buildErr) {
        applyFieldErrors([{
          field: 'form',
          section: 'basic',
          message: buildErr?.message || t('publishFlow.buildFail'),
        }]);
        return;
      }
      const sampleCurrency = samplePublishPayloadCurrency(payload);
      if (!sampleCurrency) {
        applyFieldErrors([{
          field: 'supply',
          section: 'sales',
          message: shopCaps.usesPriceInfo
            ? t('publishFlow.needSaleCurrency')
            : t('publishFlow.needSupplyCurrency'),
        }]);
        return;
      }
      // 提交前落一份完整草稿：发布记录可关联；编辑页回填不依赖审核中不全的平台详情
      let submitDraftId = draftId;
      try {
        setSubmitHint(t('publishFlow.savingDraft'));
        const draftPayload = {
          form: {
            ...submitForm,
            selectedSizeIds: (submitForm.selectedSizeIds?.length
              ? submitForm.selectedSizeIds
              : deriveSelectedSizeIds(submitForm.skcList)
            ).map((id) => String(id)),
          },
        };
        const draftRes = submitDraftId
          ? await sheinPlatformAPI.updatePublishDraft(shopId, submitDraftId, draftPayload)
          : await sheinPlatformAPI.createPublishDraft(shopId, draftPayload);
        if (draftRes?.id != null) {
          submitDraftId = String(draftRes.id);
          setDraftId(submitDraftId);
        }
      } catch (draftErr) {
        showToast(draftErr?.message || t('publishFlow.draftFailContinue'), 'warning');
      }
      const params = submitDraftId ? { draftId: Number(submitDraftId) || submitDraftId } : {};
      setSubmitHint(t('publishFlow.submittingJob'));
      let job = await sheinPlatformAPI.publishOrEditProduct(shopId, payload, params);
      const jobId = job?.jobId;
      let platformRes = job?.response;
      if (jobId && !job?.done) {
        setSubmitHint(job?.message || t('publishFlow.queueWait'));
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        let latest = job;
        while (latest && !latest.done) {
          await sleep(700);
          latest = await sheinPlatformAPI.getGoodsSubmitJob(shopId, jobId);
          if (!latest) break;
          setSubmitHint(latest.message || t('publishFlow.publishing'));
          job = latest;
        }
        platformRes = latest?.response;
        if (!platformRes && latest?.status === 'failed') {
          throw new Error(latest?.error || latest?.message || t('publishExtra.submitFail'));
        }
      } else if (!platformRes && job && (job.code != null || job.info != null)) {
        platformRes = job;
      }
      const res = platformRes || job?.response || job;
      const ok = isSheinPublishSuccess(res);
      if (!ok) {
        const fieldErrs = extractSheinPublishFieldErrors(res, extractSheinPublishFailMessage(res) || job?.error);
        applyFieldErrors(fieldErrs, extractSheinPublishFailMessage(res) || job?.message || job?.error);
        return;
      }
      setFieldErrors({});
      showToast(t('publishFlow.submittedReview'), 'success');
      const publishedSpu = extractSheinPublishedSpu(res, payload, form.spuName)
        || job?.publishedSpu
        || '';
      if (!publishedSpu) {
        showToast(t('publishFlow.noSpuRefresh'), 'warning');
      }
      if (shopId && publishedSpu) {
        sheinPlatformAPI.syncCertificates(shopId, { spuName: publishedSpu }).catch(() => {});
        showToast(t('publishFlow.certSynced'), 'info');
      }
      allowUnsavedLeave();
      navigate(buildSheinPath('products/records'), {
        replace: true,
        state: { refreshPublishRecords: true, at: Date.now() },
      });
    } catch (e) {
      const msg = e?.message || t('publishExtra.submitFail');
      applyFieldErrors([{ field: 'form', section: 'basic', message: msg }], msg);
    } finally {
      setSubmitting(false);
      setSubmitHint('');
    }
  };

  /** 编辑提交：partialEdit（非价字段）+ 按店铺类型专用改价接口 */
  const finishEditSubmit = async (submitForm, priceChanges, priceMeta = {}) => {
    setSubmitting(true);
    setPriceConfirm(null);
    try {
      // 涨价前查活动锁（priceLockType=1 强拦截）
      if (!priceChanges.empty && priceChanges.hasIncrease && shopId && submitForm.spuName) {
        setSubmitHint(t('publishFlow.checkingSiteLock'));
        try {
          const searchRes = await sheinPlatformAPI.searchProduct(shopId, {
            spuNameList: [submitForm.spuName],
            pageNum: 1,
            pageSize: 10,
          });
          const lockMap = extractPriceLocksFromSearch(searchRes);
          const lockCheck = validatePriceChangesAgainstLocks(
            priceChanges,
            lockMap,
            submitForm.selectedSites || [],
          );
          if (!lockCheck.ok) {
            throw new Error(lockCheck.message);
          }
        } catch (lockErr) {
          // search 失败不阻断降价；涨价时若明确锁失败则抛出
          if (/涨价锁定|不可涨价/.test(String(lockErr?.message || ''))) {
            throw lockErr;
          }
        }
      }

      let payload = null;
      let skippedPartial = false;
      try {
        payload = buildSheinEditPayload(submitForm, {
          baselineForm: editBaselineRef.current,
          sizeAttrs: sizeMeasureAttrs,
          skuPkgCaps,
          attrTpl,
          standardRaw: publishStandardRef.current || categoryStandard,
        });
      } catch (buildErr) {
        if (!priceChanges.empty && isEmptyPartialEditError(buildErr)) {
          skippedPartial = true;
        } else {
          throw buildErr;
        }
      }

      let submitDraftId = draftId;
      try {
        setSubmitHint(t('publishFlow.savingDraft'));
        const draftPayload = {
          form: {
            ...submitForm,
            selectedSizeIds: (submitForm.selectedSizeIds?.length
              ? submitForm.selectedSizeIds
              : deriveSelectedSizeIds(submitForm.skcList)
            ).map((id) => String(id)),
          },
        };
        const draftRes = submitDraftId
          ? await sheinPlatformAPI.updatePublishDraft(shopId, submitDraftId, draftPayload)
          : await sheinPlatformAPI.createPublishDraft(shopId, draftPayload);
        if (draftRes?.id != null) {
          submitDraftId = String(draftRes.id);
          setDraftId(submitDraftId);
        }
      } catch (draftErr) {
        showToast(draftErr?.message || t('publishFlow.draftFailContinue'), 'warning');
      }

      let partialOk = skippedPartial;
      let publishedSpu = submitForm.spuName || '';
      if (!skippedPartial && payload) {
        const params = submitDraftId ? { draftId: Number(submitDraftId) || submitDraftId } : {};
        setSubmitHint(t('publishFlow.submittingEdit'));
        let job = await sheinPlatformAPI.partialEditProduct(shopId, payload, params);
        const jobId = job?.jobId;
        let platformRes = job?.response;
        if (jobId && !job?.done) {
          setSubmitHint(job?.message || t('publishFlow.queueWait'));
          const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
          let latest = job;
          while (latest && !latest.done) {
            await sleep(700);
            latest = await sheinPlatformAPI.getGoodsSubmitJob(shopId, jobId);
            if (!latest) break;
            setSubmitHint(latest.message || t('publishFlow.editing'));
            job = latest;
          }
          platformRes = latest?.response;
          if (!platformRes && latest?.status === 'failed') {
            throw new Error(latest?.error || latest?.message || t('publishFlow.editSubmitFail'));
          }
        } else if (!platformRes && job && (job.code != null || job.info != null)) {
          platformRes = job;
        }
        const res = platformRes || job?.response || job;
        const ok = isSheinPublishSuccess(res);
        if (!ok) {
          const fieldErrs = extractSheinPublishFieldErrors(res, extractSheinPublishFailMessage(res) || job?.error);
          applyFieldErrors(fieldErrs, extractSheinPublishFailMessage(res) || job?.message || job?.error);
          return;
        }
        partialOk = true;
        publishedSpu = extractSheinPublishedSpu(res, payload, submitForm.spuName)
          || job?.publishedSpu
          || publishedSpu;
      }

      let priceMsg = '';
      if (!priceChanges.empty) {
        setSubmitHint(priceChanges.mode === 'sale' ? t('publishFlow.updatingSale') : t('publishFlow.updatingCost'));
        if (priceChanges.mode === 'cost') {
          const costBody = buildUpdateCostPayload(priceChanges, {
            reasonBySkc: priceMeta.reasonBySkc || {},
            remarkBySkc: priceMeta.remarkBySkc || {},
            fileBySkc: priceMeta.fileBySkc || {},
          });
          const chunks = chunkUpdateCostPayload(costBody);
          for (let i = 0; i < chunks.length; i += 1) {
            if (chunks.length > 1) {
              setSubmitHint(t('publishFlow.updatingCostChunk', { current: i + 1, total: chunks.length }));
            }
            const costRes = await sheinPlatformAPI.updateProductCost(shopId, chunks[i]);
            const interpreted = interpretPriceUpdateResponse(costRes, 'cost');
            if (!interpreted.ok) {
              throw new Error(interpreted.message);
            }
            priceMsg = interpreted.message;
          }
        } else if (priceChanges.mode === 'sale') {
          const saleBody = buildSaveSalePricePayload(priceChanges, {
            sites: submitForm.selectedSites || [],
            riseReason: priceMeta.saleRiseReason || '',
            siteList: submitForm.siteList || meta.sites,
          });
          const saleRes = await sheinPlatformAPI.saveProductSalePrice(shopId, saleBody);
          const interpreted = interpretPriceUpdateResponse(saleRes, 'sale');
          if (!interpreted.ok) {
            throw new Error(interpreted.message);
          }
          priceMsg = interpreted.message;
          if (interpreted.pending) {
            showToast(interpreted.message, 'warning');
          }
        }
      }

      if (!partialOk && priceChanges.empty) {
        throw new Error(t('publishFlow.noChanges'));
      }

      setFieldErrors({});
      if (partialOk && !priceChanges.empty) {
        showToast(priceMsg ? t('publishFlow.editSubmittedWith', { detail: priceMsg }) : t('publishFlow.editPriceUpdated'), 'success');
      } else if (!priceChanges.empty) {
        showToast(priceMsg || t('publishFlow.priceUpdated'), 'success');
      } else {
        showToast(t('publishFlow.editSubmitted'), 'success');
      }
      if (!publishedSpu) {
        showToast(t('publishFlow.noSpuRefresh'), 'warning');
      }
      allowUnsavedLeave();
      navigate(buildSheinPath('products/records'), {
        replace: true,
        state: { refreshPublishRecords: true, at: Date.now() },
      });
      return { ok: true };
    } catch (e) {
      const msg = e?.message || t('publishExtra.submitFail');
      applyFieldErrors([{ field: 'form', section: 'basic', message: msg }], msg);
      return { ok: false, message: msg };
    } finally {
      setSubmitting(false);
      setSubmitHint('');
    }
  };

  const allowedAttrIds = useMemo(
    () => allProductAttrs.map((attr) => String(attr.attributeId || '')).filter(Boolean),
    [allProductAttrs],
  );
  attrTplRef.current = attrTpl;

  const resolvedAttrStyleNo = useMemo(
    () => styleNoFromSupplierCode(
      form.supplierCode,
      attrTemplates.map((item) => item?.styleNo).filter(Boolean),
    ),
    [form.supplierCode, attrTemplates],
  );

  useEffect(() => {
    const target = String(form.productTypeId || '');
    if (!target || attrLoading || !allowedAttrIds.length) return undefined;
    if (attrTemplatesLoadedForRef.current !== target) return undefined;

    const styleKey = String(resolvedAttrStyleNo || '').trim().toLowerCase();
    const pendingCategory = String(pendingAttrAutoForRef.current || '') === target;
    // 首次进入编辑/回填：锁定当前款号，避免立刻重套覆盖已有属性
    if (lastAttrAutoStyleKeyRef.current === null) {
      lastAttrAutoStyleKeyRef.current = styleKey;
      if (!pendingCategory) return undefined;
    }
    const styleChanged = styleKey !== lastAttrAutoStyleKeyRef.current;
    if (!pendingCategory && !styleChanged) return undefined;

    const picked = pickAutoRuleTemplate(attrTemplates, { styleNo: resolvedAttrStyleNo });
    pendingAttrAutoForRef.current = '';
    lastAttrAutoStyleKeyRef.current = styleKey;
    if (!picked?.id) return undefined;

    let cancelled = false;
    setSelectedAttrTemplateId(String(picked.id));
    setAttrTemplateBusy(true);
    sheinPlatformAPI.getProductAttributeTemplate(shopId, picked.id)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.item?.payload || {};
        const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
        setForm((prev) => {
          if (String(prev.productTypeId) !== target) return prev;
          // 整表替换：空模板则清空类目属性
          const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current);
          return { ...prev, productAttributes: aligned };
        });
        const styleHint = resolvedAttrStyleNo ? t('templateEditor.styleHint', { styleNo: resolvedAttrStyleNo }) : '';
        showToast(
          filled
            ? t('templateEditor.autoNamed', { name: picked.name || picked.id, hint: styleHint })
            : t('templateEditor.autoEmpty'),
          'success',
        );
      })
      .catch(() => {
        /* 自动套用失败不打断发品 */
      })
      .finally(() => {
        if (!cancelled) setAttrTemplateBusy(false);
      });
    return () => { cancelled = true; };
  }, [
    shopId,
    form.productTypeId,
    resolvedAttrStyleNo,
    attrLoading,
    allowedAttrIds,
    attrTemplates,
    t,
  ]);

  const applyAttrTemplate = async (templateId) => {
    const id = String(templateId || '').trim();
    if (!shopId || !id) return;
    if (!allowedAttrIds.length) {
      showToast(t('templateEditor.attrsNotLoaded'), 'error');
      return;
    }
    setAttrTemplateBusy(true);
    try {
      const res = await sheinPlatformAPI.getProductAttributeTemplate(shopId, id);
      const item = res?.item || {};
      const payload = item.payload || {};
      const { patch, filled } = applyProductAttributeTemplatePayload(payload, allowedAttrIds);
      setForm((prev) => {
        // 整表替换：空模板则清空类目属性
        const aligned = alignAttributesToCategoryTemplate({ ...patch }, attrTplRef.current || attrTpl);
        return { ...prev, productAttributes: aligned };
      });
      setSelectedAttrTemplateVersion(parseTemplateVersion(item.version));
      showToast(filled ? t('templateEditor.attrApplied', { count: filled }) : t('templateEditor.attrEmptyCleared'), 'success');
    } catch (e) {
      showToast(e?.message || t('templateEditor.attrApplyFail'), 'error');
    } finally {
      setAttrTemplateBusy(false);
    }
  };

  const onAttrTemplateSelect = (templateId) => {
    const id = String(templateId || '').trim();
    setSelectedAttrTemplateId(id);
    if (id) {
      applyAttrTemplate(id);
      return;
    }
    setSelectedAttrTemplateVersion(null);
    // 下拉选空：直接清除类目属性
    setForm((prev) => ({ ...prev, productAttributes: {} }));
  };

  const openAttrSaveModal = () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    if (!form.categoryId || !form.productTypeId) {
      showToast(t('publishAttr.pickCategory'), 'error');
      return;
    }
    if (!countFilledProductAttributes(form.productAttributes)) {
      showToast(t('templateEditor.fillAttrsFirst'), 'error');
      return;
    }
    setAttrSaveModalOpen(true);
  };

  const refreshAttrTemplates = async () => {
    if (!shopId || !form.productTypeId) return;
    try {
      const listRes = await sheinPlatformAPI.listProductAttributeTemplates(shopId, {
        productTypeId: form.productTypeId,
        page: 1,
        pageSize: 50,
      });
      setAttrTemplates(Array.isArray(listRes?.items) ? listRes.items : []);
    } catch {
      /* ignore */
    }
  };
  const visibleOptional = useMemo(() => {
    if (attrsExpanded) return optionalAttrs;
    const hintIds = new Set(Object.keys(indexAttributeChangeHints(
      changePreviews.productAttributes,
      !!versionConflicts.productAttributes,
    )));
    const head = optionalAttrs.slice(0, 9);
    const headIds = new Set(head.map((a) => String(a.attributeId)));
    const extraTail = optionalAttrs
      .slice(9)
      .filter((a) => {
        const id = String(a.attributeId);
        return isLinkedRequired(id) || hintIds.has(id);
      })
      .filter((a) => !headIds.has(String(a.attributeId)));
    return extraTail.length ? [...head, ...extraTail] : head;
  }, [
    attrsExpanded,
    optionalAttrs,
    linkedAttrRules,
    changePreviews.productAttributes,
    versionConflicts.productAttributes,
  ]);

  const attrVersionHintById = useMemo(
    () => indexAttributeChangeHints(
      changePreviews.productAttributes,
      !!versionConflicts.productAttributes,
    ),
    [changePreviews.productAttributes, versionConflicts.productAttributes],
  );

  return (
    <div className={`spo-wizard spo-publish${submitting ? ' is-publishing' : ''}`}>
      {submitting ? (
        <div className="spo-publish-loading" role="status" aria-live="polite" aria-busy="true">
          <div className="spo-publish-loading__card">
            <i className="spo-publish-loading__spinner" aria-hidden />
            <strong>
              {isEdit ? t('publishChrome.submittingEdit') : t('publishChrome.publishing')}
            </strong>
            <span>
              {submitHint || t('publishChrome.submitWait', { platform: 'SHEIN' })}
            </span>
          </div>
        </div>
      ) : null}
      <aside className="spo-wizard__steps" aria-label={t('publishChrome.sectionsAria')}>
        <div className="spo-wizard__steps-label">
          {isEdit ? t('publishChrome.modeEdit') : t('publishChrome.modePublish')}
        </div>
        {SECTIONS.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            className={`spo-wizard__step${activeSection === item.id ? ' spo-wizard__step--active' : ''}`}
            onClick={() => scrollToSection(item.id)}
          >
            <span className="spo-wizard__step-index">{idx + 1}</span>
            <span className="spo-wizard__step-title">{t(`publishChrome.${item.titleKey}`)}</span>
          </button>
        ))}
        <div className="spo-publish__progress">
          <div className="spo-publish__progress-label">{t('publishChrome.progress', { percent: completion })}</div>
          <div className="spo-publish__progress-bar">
            <i style={{ width: `${completion}%` }} />
          </div>
        </div>
      </aside>

      <section className="spo-wizard__panel">
        <div className="spo-wizard__panel-scroll">
        {draftLoading ? (
          <p className="spo-wizard__inline-hint">
            {initialProductTemplateId || fromProductTemplate ? t('publishChrome.loadingTemplate') : t('publishChrome.loadingDraft')}
          </p>
        ) : null}
        {loadingSpu ? <p className="spo-wizard__inline-hint">{t('publishChrome.loadingDetail')}</p> : null}
        {fieldErrors.form ? (
          <div className="spo-publish-form-error" role="alert" data-field-error="form">
            {fieldErrors.form}
          </div>
        ) : null}

        {/* —— 基础信息 —— */}
        <div id="spo-pub-basic" className="spo-wizard__block" onFocusCapture={() => setActiveSection('basic')}>
          <h3>{t('publishChrome.sectionBasic')}</h3>

          <div className="spo-wizard__form">
            <label className={`spo-field spo-field--full${fieldErrors.categoryId ? ' spo-field--invalid' : ''}`} data-field-error="categoryId">
              <span className="spo-field__label">{t('publishBasic.category')} <RequiredMark /></span>
              <CategoryCascaderSelect
                categories={meta.categories}
                value={form.categoryId}
                label={form.categoryPathLabel}
                disabled={!shopId || metaLoading || isEdit}
                placeholder={!shopId ? t('returnList.pickShop') : t('publishBasic.pickCategory')}
                onChange={onCategoryChange}
              />
              {isEdit ? (
                <p className="spo-wizard__inline-hint">{t('publishBasic.categoryLocked')}</p>
              ) : null}
              <FieldErrorText message={fieldErrors.categoryId} />
            </label>

            {!isEdit && form.categoryId ? (
              <label
                className={`spo-field spo-field--full${versionConflicts.productTemplate ? ' spo-field--version-conflict' : ''}`}
                data-field-error="productTemplate"
              >
                <span className="spo-field__label">
                  {t('publishBasic.productTemplate')}
                  <TemplateVersionConflictMark
                    active={!!versionConflicts.productTemplate}
                    reloading={reloadingConflictType === TEMPLATE_VERSION_TYPES.product}
                    onReload={() => reloadConflictingTemplateRegion(TEMPLATE_VERSION_TYPES.product)}
                  />
                </span>
                <SearchableSelect
                  value={selectedProductTemplateId}
                  disabled={!shopId || productTemplateBusy || !normalizePublishProductTypeId(form.productTypeId)}
                  placeholder={
                    productTemplateBusy
                      ? t('publishBasic.loadingTemplates')
                      : (!normalizePublishProductTypeId(form.productTypeId)
                        ? t('publishBasic.noProductType')
                        : (productTemplates.length ? t('publishBasic.pickTemplate') : t('publishBasic.noTemplateForCat')))
                  }
                  emptyText={t('publishBasic.noMatchTemplate')}
                  onChange={onProductTemplateSelect}
                  options={productTemplates.map((item) => ({
                    value: String(item.id),
                    label: productTemplateOptionLabel(item, t),
                  }))}
                />
                <p className="spo-wizard__inline-hint">
                  {productTemplates.length > 1
                    ? t('publishBasic.sheinDefaultHint')
                    : (productTemplates.length === 1
                      ? t('publishBasic.autoApplied')
                      : t('publishBasic.sheinCreateHint'))}
                </p>
              </label>
            ) : (!isEdit && selectedProductTemplateId && versionConflicts.productTemplate) ? (
              <div className="spo-field spo-field--full spo-field--version-conflict" data-field-error="productTemplate">
                <TemplateVersionConflictMark
                  className="spo-version-conflict-mark--block"
                  active
                  label={t('publishBasic.templateUpdated')}
                  reloading={reloadingConflictType === TEMPLATE_VERSION_TYPES.product}
                  onReload={() => reloadConflictingTemplateRegion(TEMPLATE_VERSION_TYPES.product)}
                />
              </div>
            ) : null}

            <label className={`spo-field spo-field--full${fieldErrors.title ? ' spo-field--invalid' : ''}`} data-field-error="title">
              <span className="spo-field__label spo-field__label-row">
                <span>{t('publishBasic.productTitle')} <RequiredMark /></span>
                <button
                  type="button"
                  className="spo-btn spo-btn--danger spo-btn--sm"
                  disabled={aiTitleBusy || submitting || attrLoading}
                  title={buildCreditActionHint('aiCopywriting', 1) || undefined}
                  onClick={() => { void handleAiTitle(); }}
                >
                  {aiTitleBusy ? t('publishBasic.generating') : t('publishBasic.aiTitle')}
                </button>
              </span>
              <textarea
                className="spo-textarea spo-textarea--title"
                value={form.title}
                maxLength={150}
                rows={2}
                onChange={(e) => update('title', e.target.value)}
                placeholder={t('publishBasic.titlePhShein')}
              />
              <span className="spo-publish__counter">{String(form.title || '').length}/150</span>
              <FieldErrorText message={fieldErrors.title} />
            </label>

            <label className="spo-field">
              <span className="spo-field__label">{t('publishBasic.titleLang')}</span>
              <SearchableSelect
                value={form.titleLanguage}
                allowClear={false}
                onChange={(v) => update('titleLanguage', v)}
                placeholder={t('publishBasic.pickLang')}
                options={localizeOptions(TITLE_LANGUAGE_OPTIONS, TITLE_LANG_KEYS)}
              />
              <FieldErrorText message={fieldErrors.titleLanguage} />
            </label>
            <label className={`spo-field${fieldErrors.supplierCode ? ' spo-field--invalid' : ''}`} data-field-error="supplierCode">
              <span className="spo-field__label">{t('publishBasic.merchantSku')} <RequiredMark /></span>
              <input
                className="spo-input"
                value={form.supplierCode}
                onChange={(e) => update('supplierCode', e.target.value)}
                placeholder={t('publishBasic.merchantSkuPh')}
              />
              <FieldErrorText message={fieldErrors.supplierCode} />
            </label>
            <label className={`spo-field${fieldErrors.brandCode ? ' spo-field--invalid' : ''}`} data-field-error="brandCode">
              <span className="spo-field__label">{t('publishBasic.brand')}</span>
              <SearchableSelect
                value={form.brandCode}
                disabled={!shopId || metaLoading}
                placeholder={t('publishBasic.brandPhShein')}
                onChange={(v) => update('brandCode', v)}
                options={brandOptions
                  .filter((b) => b.brandCode && b.brandCode !== '—')
                  .map((opt) => ({
                    value: opt.brandCode,
                    label: opt.brandName !== '—' ? opt.brandName : opt.brandCode,
                  }))}
              />
              <FieldErrorText message={fieldErrors.brandCode} />
            </label>
            <label className="spo-field">
              <span className="spo-field__label">{t('publishBasic.origin')}</span>
              <SearchableSelect
                value={form.originCountry}
                placeholder={t('publishBasic.originPhShein')}
                onChange={(v) => update('originCountry', v)}
                options={ORIGIN_OPTIONS.map((c) => ({ value: c, label: c }))}
              />
            </label>
            <label className="spo-field">
              <span className="spo-field__label">{t('publishBasic.productIp')}</span>
              <SearchableSelect
                value={form.ipId}
                disabled={!shopId || metaLoading}
                placeholder={t('publishBasic.pickIp')}
                onChange={onIpChange}
                options={ipOptions
                  .filter((i) => i.ipId && i.ipId !== '—')
                  .map((opt) => ({
                    value: opt.ipId,
                    label: opt.ipNameCn !== '—' ? opt.ipNameCn : opt.ipName,
                  }))}
              />
            </label>
            <label className={`spo-field spo-field--full${fieldErrors.description ? ' spo-field--invalid' : ''}`} data-field-error="description">
              <span className="spo-field__label">{t('publishBasic.desc')}</span>
              <textarea className="spo-textarea" value={form.description} onChange={(e) => update('description', e.target.value)} placeholder={t('publishBasic.descPhShein')} />
              <FieldErrorText message={fieldErrors.description} />
            </label>
          </div>
        </div>

        {/* —— 类目属性 —— */}
        <div
          id="spo-pub-attrs"
          className="spo-wizard__block"
          onFocusCapture={() => setActiveSection('attrs')}
          data-field-error="productAttributes"
        >
          <div className="spo-publish__block-head">
            <h3>{t('publishChrome.sectionAttrs')}</h3>
          </div>
          <FieldErrorText message={fieldErrors.productAttributes} />
          {!form.categoryId ? (
            <p className="spo-wizard__inline-hint">{t('publishAttr.pickCategory')}</p>
          ) : attrLoading ? (
            <div className="spo-attr-loading" role="status" aria-live="polite" aria-busy="true">
              <div className="spo-attr-loading__banner">
                <span className="spo-attr-loading__spinner" aria-hidden />
                <div className="spo-attr-loading__copy">
                  <strong>{t('publishAttr.loadingTitle')}</strong>
                  <span>{t('publishAttr.loadingHint')}</span>
                </div>
              </div>
              <div className="spo-wizard__form spo-wizard__form--attrs spo-attr-loading__skeleton">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="spo-attr-loading__card">
                    <span className="spo-attr-loading__line spo-attr-loading__line--label" />
                    <span className="spo-attr-loading__line spo-attr-loading__line--field" />
                  </div>
                ))}
              </div>
            </div>
          ) : attrError && !(requiredAttrs.length || optionalAttrs.length) ? (
            <p className="spo-wizard__inline-error" role="alert">{attrError}</p>
          ) : (
            <>
              {attrError ? <p className="spo-wizard__inline-hint">{attrError}</p> : null}
              {allProductAttrs.length > 0 ? (
                <div className={`spo-size-chart-template-bar spo-attr-template-bar${versionConflicts.productAttributes ? ' spo-attr-template-bar--version-conflict' : ''}`}>
                  <TemplateVersionConflictMark
                    active={!!versionConflicts.productAttributes}
                    label={
                      changePreviews.productAttributes?.count != null
                        ? t('publishAttr.updatedCount', { count: changePreviews.productAttributes.count })
                        : t('photoLib.updatedMark')
                    }
                    reloading={reloadingConflictType === TEMPLATE_VERSION_TYPES.attribute}
                    resolveChangePreview={async () => (
                      changePreviews.productAttributes || buildAttrChangePreview()
                    )}
                    onReload={() => reloadConflictingTemplateRegion(TEMPLATE_VERSION_TYPES.attribute)}
                  />
                  <select
                    className="spo-select"
                    value={selectedAttrTemplateId}
                    disabled={attrTemplateBusy || !shopId}
                    onChange={(e) => onAttrTemplateSelect(e.target.value)}
                  >
                    <option value="">{t('publishAttr.pickLib')}</option>
                    {attrTemplates.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                        {item.filledAttrCount != null ? t('publishAttr.filledCount', { count: item.filledAttrCount }) : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="spo-btn spo-btn--primary spo-btn--sm"
                    disabled={attrTemplateBusy || !shopId || !form.categoryId}
                    onClick={openAttrSaveModal}
                  >
                    {t('shop.save')}
                  </button>
                </div>
              ) : null}
              {requiredAttrs.length > 0 ? (
                <>
                  <h4 className="spo-publish__subhead">
                    {t('publishAttr.requiredAttrs')}
                    {linkedAttrLoading ? <span className="spo-wizard__inline-hint">{t('publishAttr.linkSync')}</span> : null}
                  </h4>
                  <div className="spo-wizard__form spo-wizard__form--attrs">
                    {layoutAttributeFields(requiredAttrs).map(({ attr, fullWidth }) => (
                      <AttributeField
                        key={attr.attributeId}
                        attr={attr}
                        required={isSheinAttrRequiredMark(attr, requiredAttrs, form.productAttributes)}
                        fullWidth={fullWidth}
                        shopId={shopId}
                        categoryId={form.categoryId}
                        allowCustomValue={!!customAttrPerm[String(attr.attributeId)]}
                        allowedValueIds={linkedAllowedIds(attr.attributeId)}
                        value={form.productAttributes[attr.attributeId]}
                        onChange={(v) => setAttrValue(attr.attributeId, v)}
                        onCustomValueAdded={appendCustomAttrValue}
                        versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                      />
                    ))}
                  </div>
                </>
              ) : null}

              {optionalAttrs.length > 0 ? (
                <>
                  <h4 className="spo-publish__subhead">
                    {t('publishAttr.optionalAttrs')}
                    {!requiredAttrs.length && linkedAttrLoading
                      ? <span className="spo-wizard__inline-hint">{t('publishAttr.linkSync')}</span>
                      : null}
                  </h4>
                  <div className="spo-wizard__form spo-wizard__form--attrs">
                    {layoutAttributeFields(visibleOptional).map(({ attr, fullWidth }) => (
                      <AttributeField
                        key={attr.attributeId}
                        attr={attr}
                        required={isLinkedRequired(attr.attributeId)}
                        fullWidth={fullWidth}
                        shopId={shopId}
                        categoryId={form.categoryId}
                        allowCustomValue={!!customAttrPerm[String(attr.attributeId)]}
                        allowedValueIds={linkedAllowedIds(attr.attributeId)}
                        value={form.productAttributes[attr.attributeId]}
                        onChange={(v) => setAttrValue(attr.attributeId, v)}
                        onCustomValueAdded={appendCustomAttrValue}
                        versionUpdateHint={attrVersionHintById[String(attr.attributeId)] || null}
                      />
                    ))}
                  </div>
                  {optionalAttrs.length > 9 ? (
                    <div className="spo-publish__expand">
                      <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={() => setAttrsExpanded((v) => !v)}>
                        {attrsExpanded ? t('returnDetail.collapse') : t('publishAttr.expand', { count: optionalAttrs.length - 9 })}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}

              {!requiredAttrs.length && !optionalAttrs.length ? (
                <p className="spo-wizard__inline-hint">{t('publishAttr.none')}</p>
              ) : null}
            </>
          )}
        </div>

        {/* —— 规格供应 —— */}
        <div id="spo-pub-spec" className="spo-wizard__block" onFocusCapture={() => setActiveSection('spec')} data-field-error="skcList">
          <h3>{t('publishChrome.sectionSpecTitle')}</h3>
          <FieldErrorText message={fieldErrors.skcList || fieldErrors.supplierSku || fieldErrors.supply} />
          <SpecSupplySection
            colorAttr={colorAttr}
            sizeAttr={sizeAttr}
            skcList={form.skcList}
            selectedSizeIds={form.selectedSizeIds || []}
            shopId={shopId}
            categoryId={form.categoryId}
            customAttrPerm={customAttrPerm}
            onCustomValueAdded={appendCustomAttrValue}
            currency={platformCurrency}
            currencyPending={publishCurrencyLoading || !platformCurrency}
            attrLoading={attrLoading}
            isEdit={isEdit}
            priceMode={priceMode}
            priceEditable={!isEdit || Boolean(priceMode)}
            priceLockedHint={
              isEdit && !priceMode
                ? t('publishSpec.priceLocked')
                : ''
            }
            defaultSupplierSkuSpu={form.supplierCode || ''}
            printImages={form.printImages || []}
            onPrintImagesChange={updatePrintImages}
            sheinVideos={form.sheinVideos || []}
            onVideosChange={onVideosChange}
            videoError={fieldErrors.sheinVideos || ''}
            selectedSites={form.selectedSites}
            printInfringement={form.ipePrintInfringement}
            printInfringementBusy={printInfringementBusy}
            onAssessPrintInfringement={handleAssessPrintInfringement}
            infringementCreditHint={buildCreditActionHint('infringementDetection', 1) || ''}
            onColorsChange={onColorsChange}
            onSizesChange={onSizesChange}
            onUpdateSkc={updateSkc}
            onUpdateSku={updateSku}
            onRemoveSku={removeSku}
            onBatchUpdateSku={batchUpdateSku}
          />
          <SkuRrpFillPanel
            shopId={shopId}
            rule={rrpRule}
            loading={rrpRuleLoading}
            skcList={form.skcList}
            brandCode={form.brandCode}
            disabled={isEdit || submitting}
            readOnlyHint={isEdit
              ? t('publishFlow.rrpLocked')
              : t('publishFlow.rrpRuleHint')}
            onRetryRule={() => setRrpRuleNonce((n) => n + 1)}
            onUpdateSku={updateSku}
          />
          {fieldErrors.rrp ? <FieldErrorText message={fieldErrors.rrp} /> : null}
        </div>

        {/* —— 尺码包装 —— */}
        <div
          id="spo-pub-size"
          className="spo-wizard__block"
          onFocusCapture={() => setActiveSection('size')}
          data-field-error="sizeChart"
        >
          <SizeChartPanel
            shopId={shopId}
            productTypeId={form.productTypeId}
            categoryId={form.categoryId}
            categoryPathLabel={form.categoryPathLabel}
            supplierCode={form.supplierCode}
            sizeSaleAttributeId={form.sizeSaleAttributeId || sizeAttr?.attributeId || ''}
            sizeMeasureAttrs={sizeMeasureAttrs}
            sizeAttr={sizeAttr}
            selectedSizeIds={form.selectedSizeIds || []}
            skcList={form.skcList}
            sizeChart={form.sizeChart || {}}
            errorMessage={fieldErrors.sizeChart}
            onChangeCell={updateSizeCell}
            onApplyChart={applySizeChart}
            selectedTemplateId={selectedSizeChartTemplateId}
            onSelectedTemplateIdChange={setSelectedSizeChartTemplateId}
            onSelectedTemplateVersionChange={setSelectedSizeChartTemplateVersion}
            versionConflict={!!versionConflicts.sizeChart}
            conflictLabel={
              changePreviews.sizeChart?.count != null
                ? t('publishAttr.updatedCount', { count: changePreviews.sizeChart.count })
                : t('photoLib.updatedMark')
            }
            resolveChangePreview={async () => (
              changePreviews.sizeChart || buildSizeChartChangePreview()
            )}
            reloadingVersion={reloadingConflictType === TEMPLATE_VERSION_TYPES.sizeChart}
            onReloadVersionConflict={() => reloadConflictingTemplateRegion(TEMPLATE_VERSION_TYPES.sizeChart)}
          />
          {versionConflicts.sizeChart && Array.isArray(changePreviews.sizeChart?.changes)
            && changePreviews.sizeChart.changes.length > 0 ? (
            <div className="spo-version-conflict-summary">
              <TemplateChangeDiffList
                intro={t('publishFlow.sizeDiffCount', { count: changePreviews.sizeChart.count })}
                changes={changePreviews.sizeChart.changes.slice(0, 8)}
              />
            </div>
          ) : null}

          <h4 className="spo-publish__subhead">{t('publishChrome.sectionSize')}</h4>
          <div className="spo-publish__table-wrap spo-publish__table-wrap--scroll">
            <table className="spo-publish__table spo-pkg-table">
              <colgroup>
                <col className="spo-pkg-col spo-pkg-col--color" />
                <col className="spo-pkg-col spo-pkg-col--size" />
                <col className="spo-pkg-col spo-pkg-col--weight" />
                <col className="spo-pkg-col spo-pkg-col--dims" />
                {showSkuQuantity ? <col className="spo-pkg-col spo-pkg-col--qty" /> : null}
                {showSkuPackageType ? <col className="spo-pkg-col spo-pkg-col--pkg-type" /> : null}
              </colgroup>
              <thead>
                <tr>
                  <th>{colorLabel}</th>
                  <th>{sizeLabel}</th>
                  <th className="spo-pkg-table__weight">
                    <span className="spo-supply-th">
                      <span>{t('publishPkg.weight')}</span>
                      <button
                        type="button"
                        className="spo-supply-th__batch"
                        onClick={() => setPkgBatchMode('weight')}
                      >
                        {t('publishSpec.batch')}
                      </button>
                    </span>
                  </th>
                  <th className="spo-pkg-table__dims">
                    <span className="spo-supply-th">
                      <span>{t('publishPkg.pkgSize')}</span>
                      <button
                        type="button"
                        className="spo-supply-th__batch"
                        onClick={() => setPkgBatchMode('dims')}
                      >
                        {t('publishSpec.batch')}
                      </button>
                    </span>
                  </th>
                  {showSkuQuantity ? (
                    <th className="spo-pkg-table__qty">
                      <span className="spo-supply-th">
                        <span>{requireSkuQuantity ? <>{t('publishPkg.pkgQty')} <RequiredMark /></> : t('publishPkg.pkgQty')}</span>
                        <button
                          type="button"
                          className="spo-supply-th__batch"
                          onClick={() => setPkgBatchMode('quantity')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                  ) : null}
                  {showSkuPackageType ? (
                    <th className="spo-pkg-table__pkg-type">
                      <span className="spo-supply-th">
                        <span>{requireSkuPackageType ? <>{t('publishPkg.pkgType')} <RequiredMark /></> : t('publishPkg.pkgType')}</span>
                        <button
                          type="button"
                          className="spo-supply-th__batch"
                          onClick={() => setPkgBatchMode('packageType')}
                        >
                          {t('publishSpec.batch')}
                        </button>
                      </span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {form.skcList.flatMap((skc) => skc.skuRows.map((sku) => (
                  <tr key={`${skc.id}-${sku.id}`}>
                    <td>{skc.colorLabel || '—'}</td>
                    <td>
                      {sku.sizeIsCustom || sku.sizeExtraValue
                        ? (attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue) || '—')
                        : (
                          attributeValueDisplayLabel(
                            sizeAttr?.values?.find((v) => String(v.id) === String(sku.sizeValueId))
                            || sku.sizeLabel,
                          ) || '—'
                        )}
                    </td>
                    <td className="spo-pkg-table__weight">
                      <div className="spo-pkg-inline">
                        <input
                          className="spo-input spo-pkg-inline__input"
                          value={sku.weight ?? ''}
                          onChange={(e) => updateSku(skc.id, sku.id, { weight: e.target.value })}
                          placeholder={t('publishPkg.phWeight')}
                        />
                        <SearchableSelect
                          className="spo-select--unit"
                          value={form.weightUnit || 'g'}
                          allowClear={false}
                          onChange={(v) => update('weightUnit', v)}
                          options={WEIGHT_UNIT_OPTIONS}
                        />
                      </div>
                    </td>
                    <td className="spo-pkg-table__dims">
                      <div className="spo-pkg-inline spo-pkg-inline--dims">
                        <input
                          className="spo-input spo-pkg-inline__input"
                          value={sku.length ?? ''}
                          onChange={(e) => updateSku(skc.id, sku.id, { length: e.target.value })}
                          placeholder={t('publishPkg.len')}
                        />
                        <span className="spo-pkg-inline__x">×</span>
                        <input
                          className="spo-input spo-pkg-inline__input"
                          value={sku.width ?? ''}
                          onChange={(e) => updateSku(skc.id, sku.id, { width: e.target.value })}
                          placeholder={t('publishPkg.wid')}
                        />
                        <span className="spo-pkg-inline__x">×</span>
                        <input
                          className="spo-input spo-pkg-inline__input"
                          value={sku.height ?? ''}
                          onChange={(e) => updateSku(skc.id, sku.id, { height: e.target.value })}
                          placeholder={t('publishPkg.hei')}
                        />
                        <SearchableSelect
                          className="spo-select--unit"
                          value={form.dimUnit || 'cm'}
                          allowClear={false}
                          onChange={(v) => update('dimUnit', v)}
                          options={DIM_UNIT_OPTIONS}
                        />
                      </div>
                    </td>
                    {showSkuQuantity ? (
                      <td className="spo-pkg-table__qty">
                        <div className="spo-pkg-inline">
                          <input
                            className="spo-input spo-pkg-inline__input spo-pkg-inline__input--qty"
                            value={sku.quantity ?? ''}
                            inputMode="numeric"
                            onChange={(e) => updateSku(skc.id, sku.id, { quantity: e.target.value })}
                            placeholder={t('publishPkg.phQty')}
                          />
                          <SearchableSelect
                            className="spo-select--unit"
                            value={sku.quantityUnit || '1'}
                            allowClear={false}
                            onChange={(v) => updateSku(skc.id, sku.id, { quantityUnit: v })}
                            options={localizeOptions(QUANTITY_UNIT_OPTIONS, SHEIN_QTY_KEYS)}
                          />
                        </div>
                      </td>
                    ) : null}
                    {showSkuPackageType ? (
                      <td className="spo-pkg-table__pkg-type">
                        <SearchableSelect
                          value={sku.packageType === 0 || sku.packageType === '0' ? '' : (sku.packageType ?? '')}
                          allowClear
                          placeholder={t('shop.pleaseSelect')}
                          onChange={(v) => updateSku(skc.id, sku.id, {
                            packageType: v == null || v === '' ? '' : String(v),
                          })}
                          options={localizeOptions(PACKAGE_TYPE_OPTIONS, SKU_PKG_TYPE_KEYS)}
                        />
                      </td>
                    ) : null}
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
          {pkgBatchMode ? (
            <PackageBatchModal
              mode={pkgBatchMode}
              skcList={form.skcList}
              sizeAttr={sizeAttr}
              weightUnit={form.weightUnit || 'g'}
              dimUnit={form.dimUnit || 'cm'}
              onClose={() => setPkgBatchMode(null)}
              onConfirm={(result) => {
                if (!result?.applyAll && !result?.bySize) {
                  setPkgBatchMode(null);
                  return;
                }
                setForm((prev) => {
                  const next = {
                    ...prev,
                    skcList: result.applyAll
                      ? applyPackageBatchToAllSkus(prev.skcList, result.patch)
                      : applyPackageBatchBySize(prev.skcList, result.bySize),
                  };
                  if (pkgBatchMode === 'weight' && result.weightUnit) {
                    next.weightUnit = String(result.weightUnit);
                  }
                  if (pkgBatchMode === 'dims' && result.dimUnit) {
                    next.dimUnit = String(result.dimUnit);
                  }
                  return next;
                });
                showToast(
                  result.applyAll ? t('publishPkg.appliedAll') : t('publishPkg.appliedBySize'),
                  'success',
                );
                setPkgBatchMode(null);
              }}
            />
          ) : null}
        </div>

        {/* —— 销售发货 —— */}
        <div id="spo-pub-sales" className="spo-wizard__block" onFocusCapture={() => setActiveSection('sales')}>
          <h3>{t('publishChrome.sectionSalesInfo')}</h3>
          <div className={`spo-field spo-field--full${fieldErrors.selectedSites ? ' spo-field--invalid' : ''}`} data-field-error="selectedSites">
            <span className="spo-field__label">{t('tiktokPublish.listingSites')}{isEdit ? null : <RequiredMark />}</span>
            {isEdit ? (
              <>
                <div className="spo-readonly-sites" aria-label={t('tiktokPublish.listedSites')}>
                  {(form.selectedSites || []).length ? (
                    (form.selectedSites || []).map((abbr) => {
                      const site = siteOptions.find((s) => String(s.siteAbbr) === String(abbr));
                      return (
                        <span key={abbr} className="spo-tag spo-tag--ok">
                          {site?.siteName || abbr}
                        </span>
                      );
                    })
                  ) : (
                    <span className="spo-wizard__inline-hint">{t('tiktokPublish.noSiteInfo')}</span>
                  )}
                </div>
                <p className="spo-wizard__inline-hint">{t('tiktokPublish.sitesLocked')}</p>
              </>
            ) : (
              <SearchableMultiSelect
                value={form.selectedSites || []}
                onChange={onSitesChange}
                placeholder={metaLoading ? t('tiktokPublish.loading') : t('publishSales.pickSitesMulti')}
                disabled={metaLoading || siteOptions.length === 0}
                emptyText={metaLoading ? t('tiktokPublish.loading') : t('tiktokPublish.noSitesYet')}
                options={siteOptions.map((site) => ({
                  value: site.siteAbbr,
                  // 不展示站点列表币种，避免与供货价规范币种混淆
                  label: site.siteName || site.siteAbbr,
                }))}
              />
            )}
            <FieldErrorText message={fieldErrors.selectedSites} />
          </div>

          {/* 币种不单独展示：半托管/全托管由发布规范写入，自运营由站点列表写入 form.currency */}

          {suggestPriceRule.show ? (
            <div
              className={`spo-field spo-field--full${fieldErrors.suggestedRetailPrice ? ' spo-field--invalid' : ''}`}
              data-field-error="suggestedRetailPrice"
            >
              <span className="spo-field__label">
                {t('publishSales.suggestPrice')}
                {suggestPriceRule.required ? <RequiredMark /> : null}
                {form.currency ? ` (${form.currency})` : ''}
              </span>
              <input
                className="spo-input"
                inputMode="decimal"
                value={form.suggestedRetailPrice ?? ''}
                placeholder={t('publishSales.suggestPricePh')}
                onChange={(e) => update('suggestedRetailPrice', e.target.value)}
              />
              <p className="spo-wizard__inline-hint">
                {suggestPriceRule.required
                  ? t('publishSales.suggestPriceHint')
                  : t('publishSales.suggestPriceOptionalHint')}
              </p>
              <FieldErrorText message={fieldErrors.suggestedRetailPrice} />
            </div>
          ) : null}

          <h4 className="spo-publish__subhead">{t('tiktokPublish.shelfWay')}</h4>
          <div className="spo-publish__table-wrap">
            <table className="spo-publish__table spo-shelf-table">
              <colgroup>
                <col className="spo-shelf-col spo-shelf-col--color" />
                <col className="spo-shelf-col spo-shelf-col--way" />
                <col className="spo-shelf-col spo-shelf-col--time" />
              </colgroup>
              <thead>
                <tr>
                  <th>{colorLabel}</th>
                  <th>{t('tiktokPublish.shelfWay')}</th>
                  <th>{t('publishSales.hopeAt')}</th>
                </tr>
              </thead>
              <tbody>
                {form.skcList.map((skc) => (
                  <tr key={skc.id}>
                    <td>{skc.colorLabel || t('publishSales.unselected', { label: colorLabel })}</td>
                    <td>
                      <SearchableSelect
                        value={form.shelfWay}
                        allowClear={false}
                        onChange={(v) => update('shelfWay', v)}
                        options={[
                          { value: '1', label: t('publishSales.shelfNow') },
                          { value: '2', label: t('publishSales.shelfTimed') },
                        ]}
                      />
                    </td>
                    <td>
                      {form.shelfWay === '2' ? (
                        <SheinDateTimePicker
                          value={form.hopeOnSaleDate}
                          onChange={(v) => update('hopeOnSaleDate', v)}
                          placeholder={t('publishSales.pickHope')}
                        />
                      ) : (
                        <span className="spo-shelf-table__empty">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4 className="spo-publish__subhead">{t('publishSales.stockWarehouse')}</h4>
          <div className="spo-wizard__form">
            <div className="spo-field spo-field--full" data-field-error="warehouseId">
              <span className="spo-field__label">
                {t('publishSales.stockWarehouse')}{multiWarehouse ? <RequiredMark /> : null}
              </span>
              {metaLoading ? (
                <p className="spo-wizard__inline-hint">{t('tiktokPublish.loadingWarehouses')}</p>
              ) : warehouseOptions.length === 0 ? (
                <p className="spo-wizard__inline-hint">
                  {t('publishSales.noWarehousesShein')}
                </p>
              ) : multiWarehouse ? (
                <>
                  <SearchableSelect
                    value={form.warehouseId}
                    onChange={onWarehouseChange}
                    allowClear={false}
                    placeholder={t('publishSales.pickStockWh')}
                    emptyText={t('tiktokPublish.noWarehouseOption')}
                    options={warehouseOptions}
                  />
                  <p className="spo-wizard__inline-hint">
                    {t('publishSales.multiWhHint')}
                  </p>
                </>
              ) : (
                <p className="spo-wizard__inline-hint">
                  {t('publishSales.defaultWh', { name: warehouseOptions[0]?.label || form.warehouseId })}
                </p>
              )}
              <FieldErrorText message={fieldErrors.warehouseId} />
            </div>
          </div>
        </div>
        </div>

        <div className="spo-wizard__footer">
          <button
            type="button"
            className="spo-btn spo-btn--ghost"
            onClick={onSaveDraft}
            disabled={submitting || savingDraft || draftLoading || isEdit}
          >
            {savingDraft ? t('publishChrome.saving') : t('publishChrome.saveDraft')}
          </button>
          <div className="spo-wizard__footer-right">
            <button
              type="button"
              className="spo-btn spo-btn--secondary"
              onClick={openProductTplSaveModal}
              disabled={submitting || loadingSpu || draftLoading || !draftHydrated || !shopId}
            >
              {t('publishChrome.saveTemplate')}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--primary"
              onClick={onSubmit}
              disabled={submitting || loadingSpu || draftLoading || !draftHydrated}
            >
              {submitting ? t('publishChrome.submitting') : (isEdit ? t('publishChrome.submitEdit') : t('publishChrome.publishProduct'))}
            </button>
          </div>
        </div>
        <PublishScrollJumpFab prefix="spo" />
      </section>

      <SaveAttributeTemplateMetaModal
        open={attrSaveModalOpen}
        shopId={shopId}
        categoryId={form.categoryId}
        productTypeId={form.productTypeId}
        categoryPathLabel={form.categoryPathLabel}
        productAttributes={form.productAttributes}
        attrDefs={allProductAttrs}
        runWithoutUnloadPrompt={runWithoutUnloadPrompt}
        onClose={() => setAttrSaveModalOpen(false)}
        onSaved={refreshAttrTemplates}
      />
      <SaveProductTemplateMetaModal
        open={productTplSaveModalOpen}
        shopId={shopId}
        categoryId={form.categoryId}
        productTypeId={form.productTypeId}
        categoryPathLabel={form.categoryPathLabel}
        getFormSnapshot={() => persistSheinFormImageSlots(
          buildPublishFormSnapshot(applyPrintNamesToSheinForm(form)),
        )}
        runWithoutUnloadPrompt={runWithoutUnloadPrompt}
        onClose={() => setProductTplSaveModalOpen(false)}
      />
      <PriceUpdateConfirmModal
        open={Boolean(priceConfirm)}
        mode={priceConfirm?.changes?.mode || priceMode || 'cost'}
        shopId={shopId}
        changes={priceConfirm?.changes}
        onCancel={() => {
          if (!submitting) setPriceConfirm(null);
        }}
        onConfirm={async (meta) => {
          const result = await finishEditSubmit(
            priceConfirm.submitForm,
            priceConfirm.changes,
            meta,
          );
          if (!result?.ok) {
            throw new Error(result?.message || t('publishFlow.priceSubmitFail'));
          }
        }}
      />
    </div>
  );
}
